import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { generateAPI, videosAPI, settingsAPI, voiceAPI, falAPI, soundsAPI, customMusicAPI, ghostAPI, stockFootageAPI, accountManagerAPI, adminAPI, BASE_URL, getVideoUrl } from "../lib/api";
import GhostModePanel from "../components/GhostModePanel";
import { StockFootageToggle } from "../components/StockFootageToggle";
import MusicSelector from "../components/MusicSelector";
import CharacterPreviewModal from "../components/CharacterPreviewModal";
import { SimpleVoicePicker } from "../components/SimpleVoicePicker";
import { SameSettingsNote, settingsToForm } from "../components/SameSettingsNote";
import { describeVoice } from "../lib/voices";
import StoryModePanel from "../components/StoryModePanel";
import { PremiumRecommendedModal } from "../components/PremiumRecommendedModal";
import IGoAssist from "../components/IGoAssist";
import VideoPreviewModal from "../components/VideoPreviewModal";
import ContentPolicyModal from "../components/ContentPolicyModal";
import { PickFromGalleryButton } from "../components/ImageGalleryPicker";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";
import { useAuthStore } from "../lib/store";
import { useVideoNotifications } from "../hooks/useVideoNotifications";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { UpgradeModal } from "../components/UpgradeModal";
import { useLengthChoice } from "../components/LengthChoiceDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sparkles,
  Loader2,
  Plus,
  X,
  Video,
  FileText,
  Wand2,
  Check,
  Hash,
  Volume2,
  VolumeX,
  Pause,
  Zap,
  ImageIcon,
  Upload,
  Trash2,
  Music,
  Play,
  Clock,
  AlertTriangle,
  Palette,
  Layout,
  BookOpen,
  Users,
  Monitor,
  Star,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function CreateVideoPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const trend = location.state?.trend;
  
  // Get user from auth store
  const { user } = useAuthStore();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { check: checkLength, dialog: lengthDialog } = useLengthChoice();
  const createVideoChecked = async (d) => videosAPI.createVideo(await checkLength(d));
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generatingHashtags, setGeneratingHashtags] = useState(false);
  const [brandGuidelines, setBrandGuidelines] = useState(null);
  const [hashtagInput, setHashtagInput] = useState("");
  
  // Account Manager - Create for Client state
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null); // null = creating for self
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const isAccountManager = user?.role === "account_manager" || user?.role === "admin" || user?.is_admin;
  
  // Video notifications
  const { requestNotificationPermission, updateTabTitle } = useVideoNotifications();
  
  // Voice preview state
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const audioRef = useRef(null);
  const [clonedVoices, setClonedVoices] = useState([]); // User's cloned voices
  const [africanLanguages, setAfricanLanguages] = useState([]); // Khaya AI African language voices
  const [favoriteLanguages, setFavoriteLanguages] = useState([]); // User's favorite African languages
  const [playingVoicePreview, setPlayingVoicePreview] = useState(null);
  const [loadingVoicePreview, setLoadingVoicePreview] = useState(null);
  const voicePreviewAudioRef = useRef(null);
  
  // Background music state
  const [availableSounds, setAvailableSounds] = useState([]);
  const [customMusic, setCustomMusic] = useState([]);
  const [loadingSounds, setLoadingSounds] = useState(false);
  const [selectedMusicCategory, setSelectedMusicCategory] = useState("all");
  const [playingMusicId, setPlayingMusicId] = useState(null);
  const [loadingMusicPreview, setLoadingMusicPreview] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const musicAudioRef = useRef(null);
  const musicInputRef = useRef(null);

  // Check for addon purchase success and restore form data
  const [formRestored, setFormRestored] = useState(false);

  const [formData, setFormData] = useState({
    title: trend?.title || "",
    trendDescription: trend?.description || "",
    script: "",
    hashtags: trend?.hashtags || [],
    brandVoice: "casual",
    tone: "friendly",
    targetAudience: "general",
    videoDuration: "auto", // auto, 10, 15, 30, 60, 120, 180, 240, 300 (in seconds)
    voiceOption: "none", // Default to native Fal.ai audio (no separate voiceover)
    enableLipSync: true, // Enable lip-sync for presenter videos (adds 1-3 min processing)
    // Aspect ratio - platform presets
    aspectRatio: "9:16", // "9:16" (TikTok/Reels/Shorts), "16:9" (YouTube), "1:1" (Instagram), "4:5" (Instagram Feed)
    // Background music
    backgroundMusic: null, // Selected sound object { id, name, mood }
    musicVolume: 0.3, // 0-1, default 30%
    // Video generation mode - NEW
    videoMode: "standard", // "standard", "fast", "premium"
    premiumType: "hybrid", // "hybrid" (10s video + images, 1 credit) or "full" (multi-clip, more credits)
    // Quality tier for Fast mode - NEW
    qualityTier: "turbo", // "turbo" (720p, ~50% cheaper - default for cost savings) or "pro" (1080p, higher cost)
    // Video model selection - NEW
    videoModel: "h3-turbo", // "h3-turbo" (fastest, cheapest ~50-70% cheaper - DEFAULT) or "kling" (best quality)
    // Video presentation style - NEW
    presentationStyle: "auto", // "auto" (AI detects), "talking_head" (character speaks on camera), "cinematic" (visuals with voiceover)
    // fal.ai options (for premium mode)
    useFalAI: false, // Whether to use fal.ai for video generation
    falVideoStyle: "cinematic", // cinematic, anime, realistic, artistic
    visualDescription: "", // Optional user-provided visual description
    useLiteralPrompt: false, // NEW: Use description directly without AI rewriting
    // Image-to-video options
    useImageToVideo: false, // Whether to use image as video source
    sourceImageUrl: null, // URL of uploaded image (start frame)
    sourceImagePreview: null, // Preview URL for display (start frame)
    endImageUrl: null, // URL of uploaded end image (optional, for transitions)
    endImagePreview: null, // Preview URL for end image
    motionPrompt: "", // Description of motion/animation
    i2vProvider: "speed", // "quality" (fal.ai) or "speed" (ModelsLab) - default to speed for reliability
    // Brand Kit options - NEW
    applyBrandKit: false, // Apply brand kit defaults
    useBrandLogo: false, // Add logo watermark
    useBrandIntro: false, // Prepend intro video
    useBrandOutro: false, // Append outro video
    templateId: null, // Selected template ID
  });
  
  // Available video modes
  const [videoModes, setVideoModes] = useState(null);
  const [loadingModes, setLoadingModes] = useState(true);
  
  // Credits calculation state
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  
  // Script Doctor suggestions state
  const [scriptDoctorSuggestions, setScriptDoctorSuggestions] = useState(null);
  
  // Brand Kit state - NEW
  const [brandKit, setBrandKit] = useState(null);
  const [loadingBrandKit, setLoadingBrandKit] = useState(false);
  
  // Save as Template modal state - NEW
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [saveTemplateDescription, setSaveTemplateDescription] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Simple Mode UX - single prompt approach
  const [creationMode, setCreationMode] = useState("simple"); // "simple", "advanced", "story"
  const [isSimpleMode, setIsSimpleMode] = useState(true); // Keep for backward compatibility
  const [smartPrompt, setSmartPrompt] = useState(""); // Single prompt for AI to process
  const [showIGoAssist, setShowIGoAssist] = useState(false); // iGoAssist modal state
  const [isProcessingPrompt, setIsProcessingPrompt] = useState(false);
  const [simpleVoice, setSimpleVoice] = useState("auto"); // "auto" (native), "voiceover", "none" (silent)
  const [simpleVoiceId, setSimpleVoiceId] = useState(null); // chosen voiceover voice, never pre-selected
  
  // Video Preview Modal state (Two-Tier iGoAssist)
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  
  // Skip Preview toggle for power users (persisted in localStorage)
  const [skipPreview, setSkipPreview] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('igoviral_skip_preview') === 'true';
    }
    return false;
  });
  
  // Content Policy Modal state
  const [showContentPolicyModal, setShowContentPolicyModal] = useState(false);
  const [contentPolicyAnalysis, setContentPolicyAnalysis] = useState(null);
  const [lastFailedPrompt, setLastFailedPrompt] = useState("");
  
  // Persist skipPreview to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('igoviral_skip_preview', skipPreview.toString());
    }
  }, [skipPreview]);
  
  // Sync isSimpleMode with creationMode for backward compatibility
  useEffect(() => {
    setIsSimpleMode(creationMode === "simple");
  }, [creationMode]);
  
  // Analyze script for Advanced mode (debounced)
  useEffect(() => {
    // Only analyze in Advanced mode with Premium selected
    if (creationMode !== "advanced" || formData.videoMode !== "premium") {
      setScriptAnalysis(null);
      return;
    }
    
    const script = formData.script;
    if (!script || script.length < 20) {
      setScriptAnalysis(null);
      return;
    }
    
    // Debounce the analysis
    const timeoutId = setTimeout(async () => {
      setIsAnalyzingScript(true);
      try {
        const response = await fetch(`${BASE_URL}/api/generate/analyze-script`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ script })
        });
        
        if (response.ok) {
          const analysis = await response.json();
          setScriptAnalysis(analysis);
          
          // Auto-apply recommendations if multi-dialogue detected
          if (analysis.is_multi_dialogue && analysis.recommended_duration) {
            // Suggest but don't force duration change
            console.log(`[Script Analysis] Multi-dialogue detected. Recommended: ${analysis.recommended_duration}s`);
          }
        }
      } catch (error) {
        console.error("Script analysis error:", error);
      } finally {
        setIsAnalyzingScript(false);
      }
    }, 800); // 800ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [formData.script, creationMode, formData.videoMode]);
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingEndImage, setUploadingEndImage] = useState(false);
  const imageInputRef = useRef(null);
  const endImageInputRef = useRef(null);
  
  // Handle selecting image from gallery (ImageGalleryPicker) or Media Library
  const handleGalleryImageSelect = async (imageData) => {
    try {
      setUploadingImage(true);
      
      // Handle MediaPicker selection (has url property directly)
      if (imageData.url || imageData.thumbnail_url) {
        const imageUrl = imageData.url || imageData.thumbnail_url;
        setFormData((prev) => ({
          ...prev,
          sourceImageUrl: imageUrl,
          sourceImagePreview: imageUrl,
          useImageToVideo: true,
        }));
        toast.success("Image selected from Media Library!");
        setUploadingImage(false);
        return;
      }
      
      // Handle direct file upload from MediaPicker
      if (imageData.isUpload && imageData.file) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", imageData.file);
        
        const response = await fetch(`${BASE_URL}/api/upload/image`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${useAuthStore.getState().token}`,
          },
          body: formDataUpload,
        });
        
        const result = await response.json();
        
        if (result.success) {
          setFormData((prev) => ({
            ...prev,
            sourceImageUrl: result.image_url,
            sourceImagePreview: URL.createObjectURL(imageData.file),
            useImageToVideo: true,
          }));
          toast.success("Image uploaded successfully!");
        } else {
          throw new Error(result.detail || "Failed to process image");
        }
        setUploadingImage(false);
        return;
      }
      
      // Handle old gallery picker format (has file and preview)
      if (imageData.file) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", imageData.file);
        
        const response = await fetch(`${BASE_URL}/api/upload/image`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${useAuthStore.getState().token}`,
          },
          body: formDataUpload,
        });
        
        const result = await response.json();
        
        if (result.success) {
          setFormData((prev) => ({
            ...prev,
            sourceImageUrl: result.image_url,
            sourceImagePreview: imageData.preview || URL.createObjectURL(imageData.file),
            useImageToVideo: true,
          }));
          toast.success("Image selected from gallery!");
        } else {
          throw new Error(result.detail || "Failed to process image");
        }
      }
    } catch (error) {
      console.error("Gallery image error:", error);
      toast.error("Failed to use gallery image");
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Check for reference image from Image Studio navigation
  useEffect(() => {
    if (location.state?.referenceImage) {
      const imageData = location.state.referenceImage;
      // Auto-load the image if coming from Image Studio
      if (imageData.url) {
        setFormData((prev) => ({
          ...prev,
          sourceImageUrl: imageData.url,
          sourceImagePreview: imageData.url,
          useImageToVideo: true,
        }));
        toast.success("Image loaded from Image Studio");
      }
      // Clear the state to prevent re-loading on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Upgrade modal state for subscription quota errors
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeErrorType, setUpgradeErrorType] = useState(null);
  const [upgradeErrorData, setUpgradeErrorData] = useState(null);
  
  // Premium recommended modal state (when Fast mode needs Premium features)
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [premiumReason, setPremiumReason] = useState("");
  const [pendingSmartPromptResult, setPendingSmartPromptResult] = useState(null);
  
  // Script analysis state for Advanced mode
  const [scriptAnalysis, setScriptAnalysis] = useState(null);
  const [isAnalyzingScript, setIsAnalyzingScript] = useState(false);
  
  // Ghost Mode state (Viral Ghosting™)
  const [ghostModeEnabled, setGhostModeEnabled] = useState(false);
  const [ghostProfile, setGhostProfile] = useState(null);
  
  // Stock Footage state
  const [stockFootageEnabled, setStockFootageEnabled] = useState(false);
  const [stockFootageSettings, setStockFootageSettings] = useState(null);
  
  // Music state
  const [musicMode, setMusicMode] = useState("auto");  // "auto", "manual", "none"
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [musicVolume, setMusicVolume] = useState(0.25);
  
  // Character Reference state (for Fast mode character consistency)
  const [showCharacterPreview, setShowCharacterPreview] = useState(false);
  const [approvedCharacter, setApprovedCharacter] = useState(null);
  const [pendingVideoData, setPendingVideoData] = useState(null);
  const [inheritedSettings, setInheritedSettings] = useState(null);
  
  // Fetch available video modes on mount
  useEffect(() => {
    const fetchVideoModes = async () => {
      setLoadingModes(true);
      try {
        const response = await fetch(`${BASE_URL}/api/video-modes`);
        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }
        const data = await response.json();
        setVideoModes(data.modes);
      } catch (error) {
        console.error("Failed to fetch video modes:", error);
        // FALLBACK: Default to available: true so users aren't blocked
        // The backend will validate API keys at generation time
        setVideoModes({
          standard: { name: "Standard", available: true, speed: "~2-3 min" },
          fast: { name: "Fast", available: true, speed: "~1-3 min" },
          premium: { name: "Premium", available: true, speed: "~3-10 min" }
        });
      } finally {
        setLoadingModes(false);
      }
    };
    fetchVideoModes();
  }, []);

  // Fetch available clients for Account Managers
  useEffect(() => {
    const fetchClients = async () => {
      if (!isAccountManager) return;
      
      setLoadingClients(true);
      try {
        if (user?.is_admin || user?.role === "admin") {
          // Admins can see all users
          const response = await adminAPI.getUsers(0, 500);
          const clients = (response.data || []).filter(u => u.role === "user" || !u.role);
          setAvailableClients(clients);
        } else {
          // Account managers see only their assigned clients
          const response = await accountManagerAPI.getMyClients();
          setAvailableClients(response.data.clients || []);
        }
      } catch (error) {
        console.error("Failed to fetch clients:", error);
      } finally {
        setLoadingClients(false);
      }
    };
    
    fetchClients();
    
    // Check if coming from MyClientsPage with pre-selected client
    if (location.state?.clientId) {
      setSelectedClientId(location.state.clientId);
      setSelectedClient({
        id: location.state.clientId,
        name: location.state.clientName,
        email: location.state.clientEmail
      });
    }
  }, [isAccountManager, user, location.state]);

  // Fetch Brand Kit on page load
  useEffect(() => {
    const fetchBrandKit = async () => {
      setLoadingBrandKit(true);
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        
        const response = await fetch(`${BASE_URL}/api/brand-kit`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setBrandKit(data);
        }
      } catch (error) {
        console.error("Failed to fetch brand kit:", error);
      } finally {
        setLoadingBrandKit(false);
      }
    };
    fetchBrandKit();
  }, []);

  // Calculate credits needed when video mode, premium type, or duration changes
  useEffect(() => {
    const calculateCredits = async () => {
      // Parse duration
      let durationValue = 15; // default
      if (formData.videoDuration !== "auto") {
        durationValue = parseInt(formData.videoDuration) || 15;
      }
      
      // Only calculate for premium mode
      if (formData.videoMode !== "premium") {
        setCreditsNeeded(1);
        return;
      }
      
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) {
          console.warn("No auth token found for credit calculation");
          return;
        }
        
        const response = await fetch(`${BASE_URL}/api/video-credits/calculate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            video_mode: formData.videoMode,
            premium_type: formData.premiumType,
            duration: durationValue
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setCreditsNeeded(data.credits_needed);
          setCreditsRemaining(data.credits_remaining);
        } else {
          console.warn("Credit calculation failed:", response.status);
        }
      } catch (error) {
        console.error("Failed to calculate credits:", error);
      }
    };
    
    calculateCredits();
  }, [formData.videoMode, formData.premiumType, formData.videoDuration]);


  // Check for addon purchase success and restore form data
  useEffect(() => {
    const addonSuccess = searchParams.get("addon_success");
    
    if (addonSuccess === "true" && !formRestored) {
      // Check for saved form data from before checkout
      const savedState = localStorage.getItem("addon_checkout_state");
      
      if (savedState) {
        try {
          const { formData: savedFormData, timestamp } = JSON.parse(savedState);
          
          // Only restore if saved within the last 30 minutes
          const thirtyMinutes = 30 * 60 * 1000;
          if (Date.now() - timestamp < thirtyMinutes && savedFormData) {
            setFormData(prev => ({ ...prev, ...savedFormData }));
            setFormRestored(true);
            toast.success("Add-on purchased! Your video settings have been restored.");
            
            // Clear the saved state
            localStorage.removeItem("addon_checkout_state");
            
            // Clean up URL params
            searchParams.delete("addon_success");
            searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
          }
        } catch (error) {
          console.error("Failed to restore form data:", error);
        }
      } else {
        toast.success("Add-on purchased successfully!");
        // Clean up URL params even if no form data was saved
        searchParams.delete("addon_success");
        searchParams.delete("session_id");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, formRestored, setSearchParams]);
  
  // Fetch available sounds and custom music
  useEffect(() => {
    const fetchSounds = async () => {
      setLoadingSounds(true);
      try {
        const category = selectedMusicCategory === "all" ? null : selectedMusicCategory;
        const response = await soundsAPI.getTrendingSounds(category);
        setAvailableSounds(response.data);
      } catch (error) {
        console.error("Failed to fetch sounds:", error);
      } finally {
        setLoadingSounds(false);
      }
    };
    fetchSounds();
  }, [selectedMusicCategory]);

  // Fetch custom music on mount
  useEffect(() => {
    const fetchCustomMusic = async () => {
      try {
        const response = await customMusicAPI.getCustomMusic();
        setCustomMusic(response.data);
      } catch (error) {
        console.error("Failed to fetch custom music:", error);
      }
    };
    fetchCustomMusic();
  }, []);

  // Fetch user's cloned voices on mount
  useEffect(() => {
    const fetchClonedVoices = async () => {
      try {
        const response = await voiceAPI.getMyVoices();
        setClonedVoices(response.data.voices || []);
      } catch (error) {
        console.error("Failed to fetch cloned voices:", error);
      }
    };
    fetchClonedVoices();
  }, []);

  // Fetch African languages (Khaya AI) on mount
  useEffect(() => {
    const fetchAfricanLanguages = async () => {
      try {
        const response = await voiceAPI.getAfricanLanguages();
        if (response.data.success && response.data.flat_list) {
          setAfricanLanguages(response.data.flat_list);
        }
      } catch (error) {
        console.error("Failed to fetch African languages:", error);
      }
    };
    fetchAfricanLanguages();
  }, []);

  // Fetch favorite African languages
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await voiceAPI.getAfricanFavorites();
        if (response.data.success) {
          setFavoriteLanguages(response.data.favorites || []);
        }
      } catch (error) {
        console.error("Failed to fetch favorite languages:", error);
      }
    };
    fetchFavorites();
  }, []);
  
  // Sort African languages with favorites first
  const sortedAfricanLanguages = [...africanLanguages].sort((a, b) => {
    const aIsFav = favoriteLanguages.includes(a.id);
    const bIsFav = favoriteLanguages.includes(b.id);
    if (aIsFav && !bIsFav) return -1;
    if (!aIsFav && bIsFav) return 1;
    return 0;
  });
  
  // Toggle favorite language
  const toggleFavoriteLanguage = async (voiceId) => {
    const isFav = favoriteLanguages.includes(voiceId);
    try {
      if (isFav) {
        await voiceAPI.removeAfricanFavorite(voiceId);
        setFavoriteLanguages(prev => prev.filter(id => id !== voiceId));
        toast.success("Removed from favorites");
      } else {
        await voiceAPI.addAfricanFavorite(voiceId);
        setFavoriteLanguages(prev => [...prev, voiceId]);
        toast.success("Added to favorites");
      }
    } catch (error) {
      console.error("Failed to update favorite:", error);
      toast.error("Failed to update favorite");
    }
  };
  
  // Play voice preview sample
  const playVoicePreview = async (voiceId) => {
    // Stop current preview if playing
    if (voicePreviewAudioRef.current) {
      voicePreviewAudioRef.current.pause();
      voicePreviewAudioRef.current = null;
    }
    
    if (playingVoicePreview === voiceId) {
      setPlayingVoicePreview(null);
      return;
    }
    
    setLoadingVoicePreview(voiceId);
    
    try {
      const response = await voiceAPI.getAfricanVoiceSample(voiceId);
      
      if (response.data.success) {
        let audioUrl;
        
        if (response.data.audio_url) {
          audioUrl = response.data.audio_url;
        } else if (response.data.audio_data) {
          audioUrl = `data:${response.data.audio_type || 'audio/mp3'};base64,${response.data.audio_data}`;
        } else {
          throw new Error("No audio data in response");
        }
        
        const audio = new Audio(audioUrl);
        voicePreviewAudioRef.current = audio;
        
        audio.onended = () => {
          setPlayingVoicePreview(null);
          voicePreviewAudioRef.current = null;
        };
        
        audio.onerror = () => {
          toast.error("Failed to play audio sample");
          setPlayingVoicePreview(null);
          voicePreviewAudioRef.current = null;
        };
        
        await audio.play();
        setPlayingVoicePreview(voiceId);
      } else {
        throw new Error(response.data.error || "Failed to get voice sample");
      }
    } catch (error) {
      console.error("Failed to play voice preview:", error);
      toast.error("Voice preview not available");
    } finally {
      setLoadingVoicePreview(null);
    }
  };

  // Handle custom music upload
  const handleMusicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/aac', 'audio/ogg', 'audio/x-m4a'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|aac|ogg)$/i)) {
      toast.error("Invalid file type. Please upload MP3, WAV, M4A, AAC, or OGG files.");
      return;
    }

    // Validate file size (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 20MB.");
      return;
    }

    setUploadingMusic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, "")); // Remove extension

      const response = await customMusicAPI.uploadMusic(formData);
      
      // Refresh custom music list
      const musicResponse = await customMusicAPI.getCustomMusic();
      setCustomMusic(musicResponse.data);
      
      // Auto-select the uploaded music
      setFormData(prev => ({
        ...prev,
        backgroundMusic: {
          id: response.data.id,
          name: response.data.name,
          mood: "Custom",
          isCustom: true
        }
      }));
      
      toast.success(`"${response.data.name}" uploaded and selected!`);
    } catch (error) {
      console.error("Failed to upload music:", error);
      toast.error("Failed to upload music. Please try again.");
    } finally {
      setUploadingMusic(false);
      if (musicInputRef.current) {
        musicInputRef.current.value = '';
      }
    }
  };

  // Handle delete custom music
  const handleDeleteCustomMusic = async (musicId, e) => {
    e.stopPropagation();
    try {
      await customMusicAPI.deleteMusic(musicId);
      setCustomMusic(prev => prev.filter(m => m.id !== musicId));
      
      // Deselect if currently selected
      if (formData.backgroundMusic?.id === musicId) {
        setFormData(prev => ({ ...prev, backgroundMusic: null }));
      }
      
      toast.success("Music deleted");
    } catch (error) {
      console.error("Failed to delete music:", error);
      toast.error("Failed to delete music");
    }
  };

  useEffect(() => {
    const fetchGuidelines = async () => {
      try {
        const response = await settingsAPI.getBrandGuidelines();
        setBrandGuidelines(response.data);
        setFormData((prev) => ({
          ...prev,
          brandVoice: response.data.brand_voice || "casual",
          tone: response.data.tone || "friendly",
          targetAudience: response.data.target_audience || "general",
          hashtags: [...prev.hashtags, ...(response.data.preferred_hashtags || [])],
        }));
      } catch (error) {
        console.error("Failed to fetch guidelines:", error);
      }
    };
    fetchGuidelines();
  }, []);

  // Handle preselected sound from Sounds page
  useEffect(() => {
    const preselectedSound = location.state?.preselectedSound;
    if (preselectedSound) {
      setFormData((prev) => ({
        ...prev,
        backgroundMusic: {
          id: preselectedSound.id,
          name: preselectedSound.name,
          mood: preselectedSound.mood
        }
      }));
      toast.info(`Background music "${preselectedSound.name}" selected`, {
        description: "Scroll down to see it in the Background Music section"
      });
    }
  }, [location.state?.preselectedSound]);

  // Handle Script Doctor data - pre-fill form with improved script, hooks, etc.
  useEffect(() => {
    const scriptDoctorData = location.state?.scriptDoctor;
    if (scriptDoctorData?.fromScriptDoctor) {
      console.log("[CreateVideo] Received Script Doctor data:", scriptDoctorData);
      
      // Default to SIMPLE mode - user can switch to advanced if needed
      setCreationMode("simple");
      
      // Set the smart prompt for simple mode (combines title + script for AI to work with)
      const smartPromptText = scriptDoctorData.script || "";
      setSmartPrompt(smartPromptText);
      
      // Also set form data for advanced mode (in case user switches)
      setFormData(prev => ({
        ...prev,
        title: scriptDoctorData.suggestedTitle || prev.title,
        script: scriptDoctorData.script || prev.script,
        // Also set visual description to the script for advanced mode
        visualDescription: scriptDoctorData.script || prev.visualDescription,
        // Set duration if suggested
        videoDuration: scriptDoctorData.suggestedDuration ? String(scriptDoctorData.suggestedDuration) : prev.videoDuration,
      }));

      // Store hooks and CTAs for selection
      if (scriptDoctorData.hooks?.length > 0 || scriptDoctorData.ctas?.length > 0) {
        setScriptDoctorSuggestions({
          hooks: scriptDoctorData.hooks || [],
          ctas: scriptDoctorData.ctas || [],
          powerWords: scriptDoctorData.powerWords || [],
          viralScore: scriptDoctorData.viralScore
        });
      }

      toast.success("Script loaded in Simple mode!", {
        description: "Click 'Generate' to create your video, or switch to Advanced for more options"
      });
    }
  }, [location.state?.scriptDoctor]);

  // Handle prefill from Content Policy Modal (user wants to try a new prompt)
  useEffect(() => {
    const prefillScript = location.state?.prefillScript;
    const fromContentPolicy = location.state?.fromContentPolicy;
    
    if (prefillScript && fromContentPolicy) {
      console.log("[CreateVideo] Received Content Policy rewrite suggestion:", prefillScript);
      
      // Set to simple mode with the suggested prompt
      setCreationMode("simple");
      setSmartPrompt(prefillScript);
      
      toast.success("Suggested prompt loaded!", {
        description: "This version has been rewritten to avoid content policy triggers"
      });
    }
  }, [location.state?.prefillScript, location.state?.fromContentPolicy]);

  // Handle prefill from Template page
  useEffect(() => {
    const fromTemplate = location.state?.fromTemplate;
    const templateData = location.state?.template;
    
    if (fromTemplate && templateData) {
      console.log("[CreateVideo] Received Template data:", templateData);
      
      // Determine mode based on template
      if (templateData.video_mode === 'ugc') {
        setCreationMode("ugc");
      } else if (templateData.script && templateData.visual_description) {
        setCreationMode("advanced");
      } else {
        setCreationMode("simple");
      }
      
      // Set smart prompt for simple mode
      if (templateData.script) {
        setSmartPrompt(templateData.script);
      }
      
      // Set form data for advanced mode
      setFormData(prev => ({
        ...prev,
        script: templateData.script || prev.script,
        visualDescription: templateData.visual_description || prev.visualDescription,
        videoDuration: String(templateData.duration || prev.videoDuration),
        voiceOption: templateData.voice_option || prev.voiceOption,
        videoMode: templateData.video_mode || prev.videoMode,
        videoModel: templateData.video_model || prev.videoModel,
        qualityTier: templateData.quality_tier || prev.qualityTier,
      }));
      
      // Set trend data if available
      const trendData = location.state?.trend;
      if (trendData) {
        // Title from trend
        if (trendData.title) {
          setFormData(prev => ({ ...prev, title: trendData.title }));
        }
      }

      toast.success("Template loaded!", {
        description: "Your template settings have been applied"
      });
    }
  }, [location.state?.fromTemplate, location.state?.template]);


  // Handle continuation from Video Library (Continue Story feature)
  useEffect(() => {
    const continuationOf = location.state?.continuationOf;
    const prefillScript = location.state?.prefillScript;
    const prefillTitle = location.state?.prefillTitle;
    const isContinuation = location.state?.isContinuation;
    const hiddenReferenceImage = location.state?.hiddenReferenceImage;
    const storyAnalysis = location.state?.storyAnalysis;
    
    // Only handle if this is a continuation (not from Content Policy)
    if (continuationOf && prefillScript && !location.state?.fromContentPolicy) {
      console.log("[CreateVideo] Received continuation data:", {
        fromVideo: continuationOf.videoTitle,
        title: prefillTitle,
        isContinuation: isContinuation,
        hasHiddenReference: !!hiddenReferenceImage
      });
      
      // Keep Simple mode for easy user experience
      setCreationMode("simple");
      
      // Re-use the settings the previous episode was made with (user can still change them)
      const prev = location.state?.originalSettings;
      if (prev) {
        const applied = settingsToForm(prev);
        setFormData(f => ({ ...f, ...applied.form }));
        setSimpleVoice(applied.voiceMode);
        setSimpleVoiceId(applied.voiceId);
        setInheritedSettings(prev);
      }
      
      // Pre-fill the title
      if (prefillTitle) {
        setFormData(prev => ({ ...prev, title: prefillTitle }));
      }
      
      // Pre-fill the Smart Prompt with continuation premise
      setSmartPrompt(prefillScript);
      
      // Store hidden reference image for backend (not shown to user)
      // This will be sent with the video creation request for visual consistency
      if (isContinuation && hiddenReferenceImage) {
        setFormData(prev => ({ 
          ...prev, 
          isContinuation: true,
          continuationVideoId: continuationOf.videoId,
          hiddenReferenceImage: hiddenReferenceImage, // Backend will use this
          continuationSubtitle: location.state?.continuationSubtitle || "",
        }));
      }
      
      // Show helpful toast with AI assistant hint
      toast.success(`Continuing "${continuationOf.videoTitle}"`, {
        description: "Your prompt is ready! Use the AI assistant to refine it, then generate.",
        duration: 5000
      });
    }
  }, [location.state?.continuationOf, location.state?.prefillScript, location.state?.prefillTitle, location.state?.isContinuation, location.state?.hiddenReferenceImage, location.state?.fromContentPolicy]);

  // Voice preview handler
  const handleVoicePreview = async () => {
    if (isPlayingPreview && audioRef.current) {
      // Stop playing
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingPreview(false);
      return;
    }

    try {
      setLoadingPreview(true);
      
      // Get token from store - use getState() which works for async operations
      const authToken = useAuthStore.getState().token;
      
      // Create or reuse audio element
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      
      // Set up the audio source with authorization header via fetch
      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(voiceAPI.getPreviewUrl(formData.voiceOption), {
        headers
      });
      
      if (response.status === 401) {
        toast.error("Please log in to preview voices");
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to load voice preview: ${response.status}`);
      }
      
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      
      audioRef.current.src = audioUrl;
      audioRef.current.onended = () => {
        setIsPlayingPreview(false);
        URL.revokeObjectURL(audioUrl);
      };
      audioRef.current.onerror = () => {
        setIsPlayingPreview(false);
        toast.error("Failed to play voice preview");
      };
      
      await audioRef.current.play();
      setIsPlayingPreview(true);
      
    } catch (error) {
      console.error("Voice preview error:", error);
      toast.error("Failed to load voice preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  // Stop preview when voice option changes
  useEffect(() => {
    if (audioRef.current && isPlayingPreview) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingPreview(false);
    }
  }, [formData.voiceOption]);

  // Music preview handler
  const handleMusicPreview = async (sound, e) => {
    // Prevent triggering the selection onClick
    e.stopPropagation();
    
    // If already playing this sound, stop it
    if (playingMusicId === sound.id) {
      if (musicAudioRef.current) {
        musicAudioRef.current.pause();
        musicAudioRef.current.currentTime = 0;
      }
      setPlayingMusicId(null);
      return;
    }
    
    // Stop any currently playing music
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
      musicAudioRef.current.currentTime = 0;
    }
    
    setLoadingMusicPreview(sound.id);
    
    try {
      // Create audio element if not exists
      if (!musicAudioRef.current) {
        musicAudioRef.current = new Audio();
      }
      
      // Get music preview URL from backend
      const authToken = useAuthStore.getState().token;
      const response = await fetch(`${BASE_URL}/api/sounds/${sound.id}/preview`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load music preview');
      }
      
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      
      musicAudioRef.current.src = audioUrl;
      musicAudioRef.current.volume = formData.musicVolume;
      musicAudioRef.current.onended = () => {
        setPlayingMusicId(null);
        URL.revokeObjectURL(audioUrl);
      };
      musicAudioRef.current.onerror = () => {
        setPlayingMusicId(null);
        toast.error("Failed to play music preview");
      };
      
      await musicAudioRef.current.play();
      setPlayingMusicId(sound.id);
      
    } catch (error) {
      console.error("Music preview error:", error);
      toast.error("Music preview not available");
      setPlayingMusicId(null);
    } finally {
      setLoadingMusicPreview(null);
    }
  };

  // Stop music preview when category changes or component unmounts
  useEffect(() => {
    return () => {
      if (musicAudioRef.current) {
        musicAudioRef.current.pause();
        musicAudioRef.current = null;
      }
    };
  }, [selectedMusicCategory]);

  // Update music volume when slider changes
  useEffect(() => {
    if (musicAudioRef.current && playingMusicId) {
      musicAudioRef.current.volume = formData.musicVolume;
    }
  }, [formData.musicVolume, playingMusicId]);

  // Image upload handler
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, WebP, or GIF.");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    setUploadingImage(true);
    try {
      // Create form data for upload
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await fetch(`${BASE_URL}/api/upload/image`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${useAuthStore.getState().token}`,
        },
        body: formDataUpload,
      });

      const result = await response.json();

      if (result.success) {
        // Create local preview URL
        const previewUrl = URL.createObjectURL(file);
        setFormData((prev) => ({
          ...prev,
          sourceImageUrl: result.image_url,
          sourceImagePreview: previewUrl,
          useImageToVideo: true,
        }));
        toast.success("Image uploaded successfully!");
      } else {
        throw new Error(result.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    if (formData.sourceImagePreview) {
      URL.revokeObjectURL(formData.sourceImagePreview);
    }
    setFormData((prev) => ({
      ...prev,
      sourceImageUrl: null,
      sourceImagePreview: null,
      useImageToVideo: false,
      motionPrompt: "",
      endImageUrl: null,
      endImagePreview: null,
    }));
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
    if (endImageInputRef.current) {
      endImageInputRef.current.value = "";
    }
  };

  // Handle end image upload (for Kling image-to-video transitions)
  const handleEndImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, WebP, or GIF.");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    setUploadingEndImage(true);
    try {
      // Create form data for upload
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await fetch(`${BASE_URL}/api/upload/image`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${useAuthStore.getState().token}`,
        },
        body: formDataUpload,
      });

      const result = await response.json();

      if (result.success) {
        // Create local preview URL
        const previewUrl = URL.createObjectURL(file);
        setFormData((prev) => ({
          ...prev,
          endImageUrl: result.image_url,
          endImagePreview: previewUrl,
        }));
        toast.success("End frame uploaded!");
      } else {
        throw new Error(result.detail || "Upload failed");
      }
    } catch (error) {
      console.error("End image upload error:", error);
      toast.error("Failed to upload end frame");
    } finally {
      setUploadingEndImage(false);
    }
  };

  // Remove uploaded end image
  const handleRemoveEndImage = () => {
    if (formData.endImagePreview) {
      URL.revokeObjectURL(formData.endImagePreview);
    }
    setFormData((prev) => ({
      ...prev,
      endImageUrl: null,
      endImagePreview: null,
    }));
    if (endImageInputRef.current) {
      endImageInputRef.current.value = "";
    }
  };

  // Smart script detection - checks if description looks like a full script
  const detectIfScript = (text) => {
    if (!text || text.length < 50) return false;
    
    // Indicators that text is a script:
    // 1. Length > 150 characters (scripts are usually longer)
    // 2. Multiple sentences (3+ sentences)
    // 3. Contains common script patterns
    
    const sentenceCount = (text.match(/[.!?]+/g) || []).length;
    const wordCount = text.split(/\s+/).length;
    const hasMultipleParagraphs = text.includes('\n\n') || (text.match(/\n/g) || []).length >= 2;
    const hasDialoguePatterns = /["'].*["']|:.*[.!?]/.test(text);
    const hasNarrativeFlow = wordCount > 30 && sentenceCount >= 2;
    
    // Script indicators
    const isLongEnough = text.length > 150;
    const hasEnoughSentences = sentenceCount >= 3;
    const looksLikeScript = isLongEnough && (hasEnoughSentences || hasMultipleParagraphs || hasDialoguePatterns || hasNarrativeFlow);
    
    return looksLikeScript;
  };

  // Auto-detect and set script when description changes
  // ALSO: Auto-populate visual description with the original user description
  useEffect(() => {
    if (formData.trendDescription) {
      const isScript = detectIfScript(formData.trendDescription);
      
      if (isScript && !formData.script) {
        // Auto-populate script field
        setFormData(prev => ({ ...prev, script: prev.trendDescription }));
        toast.success("Script detected!", {
          description: "Your text looks like a full script. We've added it to the script field.",
          duration: 4000
        });
      }
      
      // IMPORTANT: Always use the original description for visual generation
      // This ensures the video matches what the user described, not the AI-rewritten script
      if (!formData.visualDescription || formData.visualDescription.trim() === "") {
        setFormData(prev => ({ 
          ...prev, 
          visualDescription: prev.trendDescription,
          useLiteralPrompt: true  // Enable literal mode by default for user descriptions
        }));
      }
    }
  }, [formData.trendDescription]);

  const handleGenerateScript = async () => {
    if (!formData.title || !formData.trendDescription) {
      toast.error("Please fill in the title and description first");
      return;
    }

    setGeneratingScript(true);
    try {
      // Get duration - convert "auto" to 30 seconds default
      const duration = formData.videoDuration === "auto" ? 30 : parseInt(formData.videoDuration);
      
      const response = await generateAPI.generateScript({
        trend_title: formData.title,
        trend_description: formData.trendDescription,
        brand_voice: formData.brandVoice,
        tone: formData.tone,
        target_audience: formData.targetAudience,
        duration: duration,  // Pass duration to generate appropriate length script
      });
      setFormData((prev) => ({ ...prev, script: response.data.script }));
      toast.success("Script generated successfully!");
    } catch (error) {
      console.error("Script generation error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to generate script. Please try again.";
      toast.error(errorMessage);
    } finally {
      setGeneratingScript(false);
    }
  };

  const handleGenerateHashtags = async () => {
    // Only generate if we don't already have hashtags
    if (formData.hashtags.length >= 3) {
      return;
    }

    setGeneratingHashtags(true);
    try {
      const response = await generateAPI.generateHashtags({
        title: formData.title,
        description: formData.trendDescription,
        script: formData.script,
        target_audience: formData.targetAudience,
      });
      
      if (response.data.hashtags && response.data.hashtags.length > 0) {
        // Merge with existing hashtags, avoiding duplicates
        const existingTags = formData.hashtags.map(t => t.toLowerCase());
        const newTags = response.data.hashtags.filter(
          tag => !existingTags.includes(tag.toLowerCase())
        );
        
        setFormData((prev) => ({
          ...prev,
          hashtags: [...prev.hashtags, ...newTags].slice(0, 10), // Max 10 hashtags
        }));
        
        if (newTags.length > 0) {
          toast.success(`Added ${newTags.length} suggested hashtags!`);
        }
      }
    } catch (error) {
      console.error("Hashtag generation error:", error);
      // Silently fail - user can still add hashtags manually
    } finally {
      setGeneratingHashtags(false);
    }
  };

  const handleGoToReview = async () => {
    // Generate hashtags when moving to review step
    await handleGenerateHashtags();
    setStep(3);
  };

  const handleAddHashtag = () => {
    if (hashtagInput && !formData.hashtags.includes(hashtagInput)) {
      const tag = hashtagInput.startsWith("#") ? hashtagInput : `#${hashtagInput}`;
      setFormData((prev) => ({
        ...prev,
        hashtags: [...prev.hashtags, tag],
      }));
      setHashtagInput("");
    }
  };

  const handleRemoveHashtag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      hashtags: prev.hashtags.filter((t) => t !== tag),
    }));
  };

  // Save current settings as a new template
  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    
    setSavingTemplate(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${BASE_URL}/api/brand-kit/templates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: saveTemplateName.trim(),
          description: saveTemplateDescription.trim(),
          video_mode: formData.videoMode,
          video_style: formData.falVideoStyle,
          voice_option: formData.voiceOption,
          tone: formData.tone,
          brand_voice: formData.brandVoice,
          duration: formData.videoDuration !== "auto" ? parseInt(formData.videoDuration) : null,
          hashtags: formData.hashtags,
          visual_description: formData.visualDescription,
          use_logo: formData.useBrandLogo,
          use_intro: formData.useBrandIntro,
          use_outro: formData.useBrandOutro,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update brand kit with new template
        setBrandKit(prev => ({
          ...prev,
          templates: [...(prev?.templates || []), data.template]
        }));
        
        toast.success(`Template "${saveTemplateName}" saved!`, {
          description: "You can now quickly apply these settings to future videos.",
          duration: 4000
        });
        
        // Reset and close modal
        setSaveTemplateName("");
        setSaveTemplateDescription("");
        setShowSaveTemplateModal(false);
      } else {
        throw new Error("Failed to save template");
      }
    } catch (error) {
      console.error("Save template error:", error);
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCreateVideo = async () => {
    if (!formData.title) {
      toast.error("Please fill in the video title");
      return;
    }

    // Validate image-to-video has an image uploaded
    if (formData.useImageToVideo && !formData.sourceImageUrl) {
      toast.error("Please upload an image for Image-to-Video generation");
      return;
    }

    // Request notification permission when starting video creation
    await requestNotificationPermission();
    
    // Update tab title to show video is being created
    updateTabTitle('generating');

    setLoading(true);
    try {
      // =====================================================
      // STOCK FOOTAGE VIDEO GENERATION (Automatic Mode)
      // =====================================================
      if (stockFootageEnabled && stockFootageSettings?.enabled) {
        // Call stock footage video generation API with auto_mode
        const response = await stockFootageAPI.generateVideo({
          title: formData.title,
          script: formData.script,
          stock_clips: [], // Empty - auto mode will find clips
          auto_mode: true, // Automatic footage selection
          voice_tier: stockFootageSettings.voiceTier || "free",
          voice_id: stockFootageSettings.voiceId || null,
          // Music settings
          music_mode: musicMode,
          include_music: musicMode !== "none",
          background_music_id: selectedMusic?.id || null,
          music_volume: musicVolume,
          hashtags: formData.hashtags
        });
        
        const voiceTierName = {
          "none": "No Voice",
          "free": "Free Voice (Edge TTS)",
          "premium": "Premium Voice"
        }[stockFootageSettings.voiceTier] || "Free Voice";
        
        const musicInfo = musicMode === "auto" ? "auto-matched music" : 
                         musicMode === "manual" ? `"${selectedMusic?.name}"` : "no music";
        
        toast.success(`Stock Footage video started!`, {
          description: `AI is finding matching visuals with ${voiceTierName} and ${musicInfo}. Check progress in Video Library.`,
          duration: 6000
        });
        navigate("/library");
        return;
      }
      
      // Common video data with background music
      const commonVideoData = {
        title: formData.title,
        script: formData.script,
        hashtags: formData.hashtags,
        voice_option: formData.voiceOption,
        enable_lip_sync: formData.enableLipSync, // Lip-sync toggle
        background_music: formData.backgroundMusic ? {
          id: formData.backgroundMusic.id,
          name: formData.backgroundMusic.name,
          mood: formData.backgroundMusic.mood,
        } : null,
        music_volume: formData.musicVolume,
        // Ghost Mode data (Viral Ghosting™)
        ghost_mode_enabled: ghostModeEnabled,
        ghost_profile: ghostModeEnabled && ghostProfile ? ghostProfile : null,
        // Brand Kit settings - NEW
        apply_brand_kit: formData.applyBrandKit,
        use_brand_logo: formData.useBrandLogo,
        use_brand_intro: formData.useBrandIntro,
        use_brand_outro: formData.useBrandOutro,
        template_id: formData.templateId,
        // Account Manager - Create for Client
        for_client_id: selectedClientId || null,
      };

      // If image-to-video is enabled (Kling AI)
      if (formData.useImageToVideo && formData.sourceImageUrl) {
        const targetDuration = formData.videoDuration === "auto" ? null : parseInt(formData.videoDuration);
        
        // Create video with Kling image-to-video parameters
        await createVideoChecked({
          ...commonVideoData,
          duration: targetDuration || 10,
          use_image_to_video: true,
          source_image_url: formData.sourceImageUrl,  // Start frame
          end_image_url: formData.endImageUrl || null,  // End frame (optional)
          motion_prompt: formData.motionPrompt || formData.script.substring(0, 200),
          i2v_provider: formData.i2vProvider,  // "quality" (fal.ai) or "speed" (ModelsLab)
        });
        
        const providerName = formData.i2vProvider === "quality" ? "Premium quality" : "Fast mode";
        toast.success(`Image-to-Video started in ${providerName}!`, {
          description: formData.i2vProvider === "quality" 
            ? "Generation takes 2-4 minutes. Check progress in Video Library."
            : "Generation takes 1-2 minutes. Check progress in Video Library.",
          duration: 6000
        });
        navigate("/library");
      }
      // Handle video mode selection
      else if (formData.videoMode === "fast") {
        // Fast mode using Kling 2.5 - lip sync NOT supported
        // Check if user already has a reference image uploaded
        const hasReferenceImage = formData.useImageToVideo && formData.sourceImageUrl;
        
        // Get duration from iGoAssist recommendation or user selection
        // iGoAssist calculates the correct duration based on script length
        const targetDuration = formData.videoDuration === "auto" ? null : parseInt(formData.videoDuration);
        const recommendedDuration = scriptAnalysis?.recommended_duration || null;
        
        // Use MAX(userSelected, iGoAssistRecommended) to ensure script completes
        // If user didn't select (auto), use iGoAssist recommendation
        // Default to 15s if neither is available
        let finalDuration = targetDuration || recommendedDuration || 15;
        if (recommendedDuration && recommendedDuration > finalDuration) {
          console.log(`[Fast Mode] Script needs ${recommendedDuration}s, extending from ${finalDuration}s`);
          finalDuration = recommendedDuration;
        }
        
        if (!hasReferenceImage) {
          // No reference image - show character preview modal for consistency
          
          // Store pending video data for after character approval
          setPendingVideoData({
            ...commonVideoData,
            enable_lip_sync: false,
            duration: finalDuration,
            video_mode: "fast",
            visual_description: formData.visualDescription || null,
            use_literal_prompt: formData.useLiteralPrompt,
            quality_tier: formData.qualityTier || "pro",
            video_model: formData.videoModel || "h3-turbo",
          });
          
          // Show character preview modal
          setShowCharacterPreview(true);
          setLoading(false);
          return;
        }
        
        // Has reference image - proceed directly
        await createVideoChecked({
          ...commonVideoData,
          enable_lip_sync: false, // Force OFF - Kling 2.5 doesn't support lip sync
          duration: finalDuration,
          video_mode: "fast",
          visual_description: formData.visualDescription || null,
          use_literal_prompt: formData.useLiteralPrompt,
          quality_tier: formData.qualityTier || "pro",
          video_model: formData.videoModel || "kling",
        });
        
        toast.success("Fast AI video started!", {
          description: "Generation takes 1-5 minutes per clip. Check progress in Video Library.",
          duration: 5000
        });
        navigate("/library");
      }
      else if (formData.videoMode === "premium" || formData.useFalAI) {
        // Premium mode using fal.ai
        const targetDuration = formData.videoDuration === "auto" ? null : parseInt(formData.videoDuration);
        
        // Use script analysis for Advanced mode to pass multi-dialogue info
        const isMultiDialogue = scriptAnalysis?.is_multi_dialogue || false;
        const hasSceneDescription = scriptAnalysis?.has_scene_description || false;
        const detectedSpeakers = scriptAnalysis?.speakers || [];
        const recommendedDuration = scriptAnalysis?.recommended_duration || 0;
        
        // Calculate duration from script if no recommendation available
        // Using 2.0 words per second (120 words/min) for natural speech pace
        // This matches the backend calculation and prevents script from being cut off
        const scriptWords = (formData.script || "").split(/\s+/).filter(w => w.length > 0).length;
        const estimatedDuration = Math.ceil(scriptWords / 2.0);
        
        // Start with user-selected duration, or use recommended, or estimate from script, or default to 30s
        let finalDuration = targetDuration || recommendedDuration || estimatedDuration || 30;
        
        // IMPORTANT: Use MAX of all available durations to ensure script completes fully
        if (recommendedDuration > finalDuration) {
          console.log(`[Premium Mode] iGoAssist recommends ${recommendedDuration}s, extending from ${finalDuration}s`);
          finalDuration = recommendedDuration;
        }
        if (estimatedDuration > finalDuration) {
          console.log(`[Premium Mode] Script estimate is ${estimatedDuration}s, extending from ${finalDuration}s`);
          finalDuration = estimatedDuration;
        }
        
        // Round up to nearest 10 seconds (clip boundary)
        finalDuration = Math.ceil(finalDuration / 10) * 10;
        
        // For Full Premium mode, cap at 60s (6 clips max)
        if (formData.premiumType === "full") {
          finalDuration = Math.min(finalDuration, 60);
        }
        
        console.log(`[Premium Mode] Final duration calculation: user=${targetDuration}, recommended=${recommendedDuration}, estimated=${estimatedDuration}, final=${finalDuration}s`);
        
        // For scene-based prompts, use the script as visual_description
        // This ensures scenes are generated instead of talking heads
        const visualDesc = (isMultiDialogue || hasSceneDescription) 
          ? (formData.script || formData.visualDescription || null)
          : (formData.visualDescription || null);
        
        const response = await createVideoChecked({
          ...commonVideoData,
          duration: finalDuration,
          video_mode: "premium",
          premium_type: formData.premiumType,
          use_fal_ai: true,
          fal_video_style: formData.falVideoStyle,
          visual_description: visualDesc,
          use_literal_prompt: formData.useLiteralPrompt,
          // Presentation style - user choice for talking head vs cinematic
          presentation_style: formData.presentationStyle,
          use_native_audio: formData.presentationStyle === "cinematic" 
            ? false 
            : formData.presentationStyle === "talking_head" 
              ? true 
              : isMultiDialogue,  // Auto mode: use native audio for multi-dialogue
          // Multi-dialogue and scene support from script analysis
          is_multi_dialogue: formData.presentationStyle === "cinematic" ? false : isMultiDialogue,
          is_dialogue: formData.presentationStyle === "talking_head" ? true : (formData.presentationStyle === "cinematic" ? false : isMultiDialogue),
          has_scene_description: hasSceneDescription,
          characters: detectedSpeakers.map(name => ({ name, description: "" })),
        });
        
        // Only show success and navigate if we got a valid response
        if (response?.data?.id || response?.data?.success !== false) {
          const creditsMsg = creditsNeeded > 1 ? ` (${creditsNeeded} credits)` : "";
          const modeMsg = formData.premiumType === "full" ? "Full Premium" : "Premium";
          const styleMsg = formData.presentationStyle === "cinematic" 
            ? " (Cinematic with voiceover)" 
            : formData.presentationStyle === "talking_head" 
              ? " (Talking head)" 
              : "";
          toast.success(`${modeMsg} AI video started!${creditsMsg}${styleMsg}`, {
            description: "Generation takes 3-10 minutes. Check progress in Video Library.",
            duration: 6000
          });
          navigate("/library");
        } else {
          throw new Error(response?.data?.detail || "Failed to start video generation");
        }
      } else {
        // Standard mode using MoviePy
        const response = await createVideoChecked({
          ...commonVideoData,
          duration: formData.videoDuration === "auto" ? null : parseInt(formData.videoDuration),
          video_mode: "standard",
          visual_description: formData.visualDescription || formData.trendDescription || null, // Pass original description
          use_literal_prompt: formData.useLiteralPrompt,
        });
        
        // Only show success and navigate if we got a valid response
        if (response?.data?.id || response?.data?.success !== false) {
          toast.success("Video is being generated! Check your library in a minute.");
          navigate("/library");
        } else {
          throw new Error(response?.data?.detail || "Failed to start video generation");
        }
      }
    } catch (error) {
      console.error("Create video error:", error);
      
      // Check for subscription quota errors (403)
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail && typeof detail === 'object') {
          // Show upgrade modal for quota-related errors
          const errorType = detail.error || 'video_limit_reached';
          setUpgradeErrorType(errorType);
          setUpgradeErrorData(detail);
          setUpgradeModalOpen(true);
          return;
        }
      }
      
      let errorMessage = "Failed to create video";
      
      // Check for specific error types
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Timeout - video may still be processing, redirect to library
        toast.warning("Request timed out, but your video may still be generating. Check the library.", {
          duration: 8000
        });
        navigate("/library");
        return;
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check for video mode specific issues
        if (typeof detail === 'string') {
          if (detail.includes('No user found') || detail.includes('Key ID') || detail.includes('fal')) {
            errorMessage = "Premium mode is not available. Please try Standard or Fast mode.";
          } else if (detail.includes('Fast video')) {
            errorMessage = "Fast mode is not available. Please try Standard or Premium mode.";
          } else {
            errorMessage = detail;
          }
        } else if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;
        }
      } else if (error.message?.includes('No user found') || error.message?.includes('fal')) {
        errorMessage = "Premium mode is not available. Please try Standard or Fast mode.";
      } else if (error.message && !error.message.includes('Network')) {
        errorMessage = error.message;
      }
      
      console.error("[CREATE VIDEO] Error details:", { errorMessage, status: error.response?.status, data: error.response?.data });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handler for when character reference is approved
  const handleCharacterApproved = async (characterData) => {
    setShowCharacterPreview(false);
    setApprovedCharacter(characterData);
    
    if (!pendingVideoData) {
      toast.error("Video data not found. Please try again.");
      return;
    }
    
    setLoading(true);
    try {
      // Build the character reference URL based on source
      let characterRefUrl = characterData.full_image_url || characterData.image_url;
      
      // If it's from the generated preview (relative URL), prepend BASE_URL
      if (characterData.source === "generated" && characterData.image_url && !characterData.image_url.startsWith('http')) {
        characterRefUrl = `${BASE_URL}${characterData.image_url}`;
      } else if (characterRefUrl && characterRefUrl.startsWith('/')) {
        characterRefUrl = `${BASE_URL}${characterRefUrl}`;
      }
      
      // Create video with the approved character reference
      await createVideoChecked({
        ...pendingVideoData,
        // Add character reference for consistency
        character_ref_url: characterRefUrl,
        character_ref_id: characterData.image_id,
        character_description: characterData.character_description,
        use_character_ref: true,
      });
      
      const sourceLabel = characterData.source === "library" ? "from library" : 
                          characterData.source === "upload" ? "from upload" : "AI-generated";
      
      toast.success(`Fast AI video started with ${sourceLabel} character!`, {
        description: "Your character will be consistent across all scenes. Check progress in Video Library.",
        duration: 6000
      });
      
      // Clean up
      setPendingVideoData(null);
      setApprovedCharacter(null);
      navigate("/library");
    } catch (error) {
      console.error("Error creating video with character:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create video. Please try again.");
      setLoading(false);
    }
  };

  // Handler for Simple Mode video creation
  const handleSimpleModeCreate = async () => {
    if (!smartPrompt.trim()) {
      toast.error("Please describe what video you want to create");
      return;
    }
    if (simpleVoice === "voiceover" && !simpleVoiceId) {
      toast.error("Pick a voiceover voice", { description: "Or choose \"Character speaks\" to use the character's own voice." });
      return;
    }

    // Power user: Skip preview and go directly to creation
    if (skipPreview) {
      await proceedWithSmartPrompt(smartPrompt);
      return;
    }

    // Step 1: Auto-enhance the prompt and show preview modal
    setIsEnhancing(true);
    
    try {
      const response = await fetch(`${BASE_URL}/api/igoassist/auto-enhance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          prompt: smartPrompt
        })
      });

      if (!response.ok) {
        // Fallback: skip preview and go directly to creation
        console.warn("[Preview] Auto-enhance failed, proceeding without preview");
        await proceedWithSmartPrompt(smartPrompt);
        return;
      }

      const previewResult = await response.json();
      
      if (previewResult.success) {
        setPreviewData(previewResult);
        setShowPreviewModal(true);
      } else {
        // Fallback: skip preview and go directly to creation
        console.warn("[Preview] Auto-enhance unsuccessful, proceeding without preview");
        await proceedWithSmartPrompt(smartPrompt);
      }
      
    } catch (error) {
      console.error("Error in auto-enhance:", error);
      // Fallback: skip preview and go directly to creation
      await proceedWithSmartPrompt(smartPrompt);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Called when user confirms in the preview modal
  const handlePreviewProceed = async (enhancedPrompt) => {
    setShowPreviewModal(false);
    await proceedWithSmartPrompt(enhancedPrompt);
  };

  // Opens the full iGoAssist optimizer from the preview modal
  const handleOpenFullOptimizer = () => {
    setShowPreviewModal(false);
    setShowIGoAssist(true);
  };

  // The actual video creation flow (previously in handleSimpleModeCreate)
  const proceedWithSmartPrompt = async (promptToUse) => {
    setIsProcessingPrompt(true);
    
    try {
      // Step 1: Process the smart prompt with AI
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      
      // Get user's selected duration (null if auto)
      const userSelectedDuration = formData.videoDuration === "auto" ? null : parseInt(formData.videoDuration);
      
      const response = await fetch(`${BASE_URL}/api/generate/smart-prompt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: promptToUse,
          video_mode: formData.videoMode,
          voice_preference: simpleVoice === "voiceover" ? simpleVoiceId : simpleVoice,
          user_duration: userSelectedDuration  // Pass user's selected duration
        })
      });

      if (!response.ok) {
        throw new Error("Failed to process your idea");
      }

      const result = await response.json();
      
      // Determine final duration: use MAX(user_selected, ai_recommended)
      // This ensures user gets at least what they asked for, but longer if script needs it
      const aiDuration = result.duration || 10;
      let finalDuration = aiDuration;
      if (formData.isContinuation && userSelectedDuration) {
        finalDuration = userSelectedDuration; // keep the episode length the user set / inherited
      } else if (userSelectedDuration && userSelectedDuration > aiDuration) {
        console.log(`[Simple Mode] User requested ${userSelectedDuration}s, AI suggested ${aiDuration}s - using user's selection`);
        finalDuration = userSelectedDuration;
      }
      
      // Update form data with AI-generated values, but preserve user's duration if longer
      setFormData((prev) => ({
        ...prev,
        title: result.title,
        script: result.script,
        trendDescription: promptToUse,
        visualDescription: result.visual_description,
        hashtags: result.hashtags,
        voiceOption: result.voice_option,
        falVideoStyle: result.video_style,
        videoDuration: String(finalDuration),
      }));
      
      // Update result object with final duration for proceedWithVideoCreation
      result.duration = finalDuration;

      setIsProcessingPrompt(false);
      
      // Step 2: Check if Pro quality tier is recommended for this content
      // Show suggestion if user is on Turbo but content needs Pro
      if (result.recommended_quality_tier === "pro" && formData.qualityTier === "turbo" && formData.videoMode === "fast") {
        // Show a toast suggestion instead of blocking - let user decide
        toast.info(
          <div>
            <p className="font-medium">💡 Pro Quality Recommended</p>
            <p className="text-sm">{result.tier_reason || "This content may look better with Pro quality"}</p>
          </div>,
          { duration: 5000 }
        );
      }
      
      // Step 3: Check if Premium is required/recommended
      // This happens when: script is long (from Script Doctor), or AI detects dialogue/talking-head
      if (result.requires_premium) {
        // Store the result for later use and show the Premium recommendation modal
        setPendingSmartPromptResult(result);
        setPremiumReason(result.premium_reason || "This content requires Premium mode for best results");
        setPremiumModalOpen(true);
        return;
      }
      
      // Step 3: Proceed with video creation based on selected mode
      await proceedWithVideoCreation(result, formData.videoMode);
      
    } catch (error) {
      console.error("Error in simple mode create:", error);
      toast.error("Failed to create video. Please try again.");
      setIsProcessingPrompt(false);
      setLoading(false);
    }
  };
  
  // Helper function to proceed with video creation after mode selection
  const proceedWithVideoCreation = async (result, videoMode) => {
    // Check if this is a dialogue/talking-head video
    const isDialogue = result.is_dialogue || result.use_lip_sync || result.video_type === "dialogue";
    // Check if this is a multi-character dialogue
    const isMultiDialogue = result.is_multi_dialogue || result.video_type === "multi_dialogue";
    // Get characters for multi-dialogue
    const characters = result.characters || [];
    
    // Check if this is a story continuation (hidden Image-to-Video for visual consistency)
    const isContinuation = formData.isContinuation || false;
    const hiddenReferenceImage = formData.hiddenReferenceImage || null;
    const continuationVideoId = formData.continuationVideoId || null;
    
    if (isMultiDialogue) {
      console.log("[CREATE-VIDEO] Multi-character dialogue detected:", { characters, isMultiDialogue });
    }
    
    if (isContinuation && hiddenReferenceImage) {
      console.log("[CREATE-VIDEO] Story continuation - using hidden reference image for visual consistency");
    }
    
    if (videoMode === "fast") {
      // Prepare video data for Fast mode
      // Note: Fast mode doesn't support lip-sync well, so dialogue videos should use Premium
      const videoData = {
        title: result.title,
        script: result.script,
        hashtags: result.hashtags,
        duration: result.duration,
        voice_option: result.voice_option,
        enable_lip_sync: false,
        video_mode: "fast",
        quality_tier: formData.qualityTier || "turbo",  // Pass quality tier
        video_model: formData.videoModel || "kling",  // Pass video model
        visual_description: result.visual_description,
        background_music: null,
        music_volume: 0.3,
        is_dialogue: isDialogue,  // Pass dialogue flag to backend
        is_multi_dialogue: isMultiDialogue,  // Pass multi-dialogue flag
        characters: characters,  // Pass character descriptions
        aspect_ratio: formData.aspectRatio,  // Pass aspect ratio
        // Story continuation data (hidden Image-to-Video)
        is_continuation: isContinuation,
        continuation_video_id: continuationVideoId,
        hidden_reference_image: hiddenReferenceImage, // Backend will use for visual consistency
        continuation_subtitle: formData.continuationSubtitle || null,
      };
      
      if (isContinuation && hiddenReferenceImage) {
        // Next episode starts from the previous episode's ending frame - no new character photo
        setLoading(true);
        try {
          await createVideoChecked(videoData);
          toast.success("Next episode started from where the last one ended!", { duration: 5000 });
          navigate("/library");
        } catch (error) {
          const detail = error.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Failed to create video. Please try again.");
          setLoading(false);
        }
        return;
      }
      setPendingVideoData(videoData);
      setShowCharacterPreview(true);
    } else {
      // Premium mode - create directly
      setLoading(true);
      
      try {
        await createVideoChecked({
          title: result.title,
          script: result.script,
          hashtags: result.hashtags,
          duration: result.duration,
          voice_option: result.voice_option,
          enable_lip_sync: true,
          // Presentation style determines if we use native audio (talking head) or voiceover (cinematic)
          // "auto" = AI decides based on isDialogue, "talking_head" = force native audio, "cinematic" = force voiceover
          use_native_audio: formData.presentationStyle === "cinematic" 
            ? false 
            : formData.presentationStyle === "talking_head" 
              ? true 
              : (isDialogue || isMultiDialogue),  // Auto mode: AI decides
          presentation_style: formData.presentationStyle,  // Pass to backend
          video_mode: "premium",
          premium_type: isContinuation ? (formData.premiumType || "hybrid") : undefined,
          use_fal_ai: true,  // Required for premium mode
          visual_description: result.visual_description,
          fal_video_style: result.video_style || "realistic",
          background_music: null,
          music_volume: 0.3,
          is_dialogue: formData.presentationStyle === "talking_head" ? true : (formData.presentationStyle === "cinematic" ? false : isDialogue),
          is_multi_dialogue: formData.presentationStyle === "cinematic" ? false : isMultiDialogue,  // Cinematic mode = no multi-dialogue
          characters: characters,  // Pass character descriptions for multi-character videos
          video_type: result.video_type,  // Pass video type for backend to handle appropriately
          aspect_ratio: formData.aspectRatio,  // Pass aspect ratio
          // Story continuation data (hidden Image-to-Video)
          is_continuation: isContinuation,
          continuation_video_id: continuationVideoId,
          hidden_reference_image: hiddenReferenceImage, // Backend will use for visual consistency
        continuation_subtitle: formData.continuationSubtitle || null,
        });
        
        const continuationMsg = isContinuation ? " Continuing your story with visual consistency!" : "";
        const presentationMsg = formData.presentationStyle === "cinematic" 
          ? "Creating cinematic video with voiceover narration."
          : formData.presentationStyle === "talking_head"
            ? "Creating talking-head video with lip-sync."
            : isMultiDialogue 
              ? "Creating multi-character dialogue video with lip-sync."
              : isDialogue 
                ? "Creating talking-head video with lip-sync."
                : "Creating cinematic video.";
        
        toast.success("Premium video started!", {
          description: `${presentationMsg} Check Video Library in 3-5 minutes.${continuationMsg}`,
          duration: 5000
        });
        
        // Small delay before redirect so user sees the message
        setTimeout(() => navigate("/library"), 1500);
      } catch (error) {
        console.error("Error creating video:", error);
        toast.error("Failed to create video. Please try again.");
        setLoading(false);
      }
    }
  };
  
  // Handler when user chooses to switch to Premium from the recommendation modal
  const handleSwitchToPremium = async () => {
    setPremiumModalOpen(false);
    if (pendingSmartPromptResult) {
      // Update form to Premium mode
      setFormData(prev => ({ ...prev, videoMode: "premium" }));
      // Proceed with Premium mode
      await proceedWithVideoCreation(pendingSmartPromptResult, "premium");
      setPendingSmartPromptResult(null);
    }
  };
  
  // Handler when user chooses to continue with Fast mode despite recommendation
  const handleContinueWithFast = async () => {
    setPremiumModalOpen(false);
    if (pendingSmartPromptResult) {
      // Proceed with Fast mode anyway
      await proceedWithVideoCreation(pendingSmartPromptResult, "fast");
      setPendingSmartPromptResult(null);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-4xl mx-auto space-y-8"
      data-testid="create-video-page"
    >
      {lengthDialog}
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2 md:gap-3">
          <Video className="w-6 h-6 md:w-8 md:h-8 text-[#00F0FF]" />
          Create Video
        </h1>
        <p className="text-muted-foreground mt-1 md:mt-2 text-sm md:text-base">
          Generate AI-powered scripts and create viral content.
        </p>
      </div>

      {/* Account Manager - Client Selection */}
      {isAccountManager && (
        <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                <Label className="text-white font-medium">Creating video for:</Label>
              </div>
              <Select 
                value={selectedClientId || "self"} 
                onValueChange={(value) => {
                  if (value === "self") {
                    setSelectedClientId(null);
                    setSelectedClient(null);
                  } else {
                    setSelectedClientId(value);
                    const client = availableClients.find(c => c.id === value);
                    setSelectedClient(client);
                  }
                }}
              >
                <SelectTrigger className="w-[300px] bg-secondary/50 border-border/50" data-testid="client-selector">
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <span className="text-xs text-cyan-400">Me</span>
                      </div>
                      <span>Myself ({user?.email})</span>
                    </div>
                  </SelectItem>
                  {loadingClients ? (
                    <SelectItem value="loading" disabled>
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading clients...
                      </div>
                    </SelectItem>
                  ) : availableClients.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No clients assigned
                    </SelectItem>
                  ) : (
                    availableClients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center gap-2">
                          {client.picture ? (
                            <img src={client.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-xs">{client.name?.[0]?.toUpperCase() || "?"}</span>
                            </div>
                          )}
                          <span>{client.name || client.email}</span>
                          <span className="text-xs text-muted-foreground">({client.email})</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedClient && (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  Creating for {selectedClient.name || selectedClient.email}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Steps */}
      <div className="flex items-center gap-2 md:gap-4 overflow-x-auto pb-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <div
              className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium transition-colors ${
                step >= s
                  ? "bg-[#00F0FF] text-[#0A0A0B]"
                  : "bg-secondary text-muted-foreground"
              }`}
            >
              {step > s ? <Check className="w-3 h-3 md:w-4 md:h-4" /> : s}
            </div>
            <span className={`text-xs md:text-sm whitespace-nowrap ${step >= s ? "text-white" : "text-muted-foreground"}`}>
              {s === 1 ? "Content" : s === 2 ? "Script" : "Review"}
            </span>
            {s < 3 && <div className="w-6 md:w-12 h-0.5 bg-secondary mx-1 md:mx-2" />}
          </div>
        ))}
      </div>

      {/* Step 1: Content Details */}
      {step === 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#00F0FF]" />
                  {creationMode === "simple" ? "Create Your Video" : creationMode === "story" ? "Story Mode" : "Content Details"}
                </CardTitle>
                {/* Simple/Advanced/Story Mode Toggle */}
                <div className="flex items-center gap-1 bg-secondary/30 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setCreationMode("simple")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      creationMode === "simple"
                        ? "bg-[#00F0FF] text-[#0A0A0B]" 
                        : "text-muted-foreground hover:text-white"
                    }`}
                    data-testid="simple-mode-btn"
                  >
                    Simple
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreationMode("advanced")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      creationMode === "advanced"
                        ? "bg-[#A855F7] text-white" 
                        : "text-muted-foreground hover:text-white"
                    }`}
                    data-testid="advanced-mode-btn"
                  >
                    Advanced
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreationMode("story")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                      creationMode === "story"
                        ? "bg-[#10B981] text-white" 
                        : "text-muted-foreground hover:text-white"
                    }`}
                    data-testid="story-mode-btn"
                  >
                    <BookOpen className="w-3 h-3" />
                    Story
                  </button>
                </div>
              </div>
              {creationMode === "simple" && (
                <p className="text-sm text-muted-foreground mt-2">
                  Just tell us what video you want - AI handles the rest!
                </p>
              )}
              {creationMode === "story" && (
                <p className="text-sm text-muted-foreground mt-2">
                  Create multi-scene stories with consistent characters
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ===== STORY MODE UI ===== */}
              {creationMode === "story" ? (
                <StoryModePanel 
                  onVideoCreated={(videoId) => {
                    // Navigate to media library video section to show the new video
                    navigate(`/media-library?type=video&highlight=${videoId}`);
                  }}
                />
              ) : (
              <>
              {/* ===== SIMPLE MODE UI ===== */}
              {isSimpleMode ? (
                <div className="space-y-6">
                  {/* Single Smart Prompt */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white text-lg flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#00F0FF]" />
                        What video do you want to create?
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowIGoAssist(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#00F0FF] bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 rounded-full border border-[#00F0FF]/30 transition-colors"
                        data-testid="igoassist-optimize-btn"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                        Optimize with iGoAssist
                      </button>
                    </div>
                    <Textarea
                      placeholder="Just describe your video idea! Examples:

• 'A fitness coach showing 3 quick morning stretches in a sunny gym'

• 'Funny video about why Mondays feel so long'

• 'A chef making the perfect avocado toast, close-up food shots'

• 'Product showcase of my new skincare serum with before/after'

Be as specific or vague as you like - AI will fill in the details!"
                      value={smartPrompt}
                      onChange={(e) => setSmartPrompt(e.target.value)}
                      className="min-h-[160px] bg-secondary/50 border-[#00F0FF]/30 focus:border-[#00F0FF]/50 text-base"
                      data-testid="smart-prompt-input"
                    />
                  </div>

                  {/* iGoAssist Modal for Simple Mode */}
                  <IGoAssist
                    isOpen={showIGoAssist}
                    onClose={() => setShowIGoAssist(false)}
                    initialPrompt={smartPrompt}
                    currentPage="create-video-simple"
                    onOptimizedPrompt={(optimized) => {
                      setSmartPrompt(optimized);
                      toast.success("Prompt optimized!", { description: "Your improved prompt is ready" });
                    }}
                  />

                  {/* Video Preview Modal - Two-Tier iGoAssist */}
                  <VideoPreviewModal
                    isOpen={showPreviewModal}
                    onClose={() => setShowPreviewModal(false)}
                    previewData={previewData}
                    onProceed={handlePreviewProceed}
                    onOpenFullOptimizer={handleOpenFullOptimizer}
                    isCreating={isProcessingPrompt || loading}
                  />

                  {inheritedSettings && formData.isContinuation && (
                    <SameSettingsNote settings={inheritedSettings} form={formData} />
                  )}

                  {/* Voice Selection - native, voiceover (explicit voice pick), or silent */}
                  <SimpleVoicePicker
                    mode={simpleVoice}
                    onModeChange={setSimpleVoice}
                    voiceId={simpleVoiceId}
                    onVoiceChange={setSimpleVoiceId}
                    clonedVoices={clonedVoices}
                  />

                  {/* Video Mode Selection - Simplified */}
                  <div className="space-y-3">
                    <Label className="text-white text-sm">Video Quality</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, videoMode: "fast" }))}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          formData.videoMode === "fast"
                            ? "border-[#F59E0B] bg-[#F59E0B]/10"
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="simple-mode-fast"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="w-4 h-4 text-[#F59E0B]" />
                          <span className="font-medium text-white">Fast</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Quick generation, 1-3 min</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, videoMode: "premium" }))}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          formData.videoMode === "premium"
                            ? "border-[#A855F7] bg-[#A855F7]/10"
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="simple-mode-premium"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-4 h-4 text-[#A855F7]" />
                          <span className="font-medium text-white">Premium</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Best quality, 3-5 min</p>
                      </button>
                    </div>
                  </div>

                  {/* Quality Tier Selection - Shows when Fast is selected */}
                  {formData.videoMode === "fast" && (
                    <div className="space-y-3">
                      <Label className="text-white text-sm flex items-center gap-2">
                        Quality Tier
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">saves credits</span>
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, qualityTier: "turbo" }))}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            formData.qualityTier === "turbo"
                              ? "border-[#10B981] bg-[#10B981]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid="simple-quality-turbo"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Zap className="w-4 h-4 text-[#10B981]" />
                            <span className="font-medium text-white text-sm">Turbo</span>
                            <span className="text-[10px] px-1 py-0.5 bg-[#10B981]/20 text-[#10B981] rounded">Default</span>
                          </div>
                          <p className="text-xs text-muted-foreground">720p • Fast & affordable</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, qualityTier: "pro" }))}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            formData.qualityTier === "pro"
                              ? "border-[#F59E0B] bg-[#F59E0B]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid="simple-quality-pro"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Star className="w-4 h-4 text-[#F59E0B]" />
                            <span className="font-medium text-white text-sm">Pro</span>
                          </div>
                          <p className="text-xs text-muted-foreground">1080p • Best quality</p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Video Model Selection - For Fast mode */}
                  {formData.videoMode === "fast" && (
                    <div className="space-y-3">
                      <Label className="text-white text-sm flex items-center gap-2">
                        AI Model
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">choose engine</span>
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, videoModel: "h3-turbo" }))}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            formData.videoModel === "h3-turbo"
                              ? "border-[#10B981] bg-[#10B981]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid="simple-model-h3-turbo"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Zap className="w-4 h-4 text-[#10B981]" />
                            <span className="font-medium text-white text-sm">H3 Turbo</span>
                            <span className="text-[10px] px-1 py-0.5 bg-[#10B981]/20 text-[#10B981] rounded">Default</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Fastest • 50% cheaper • Native audio</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, videoModel: "kling" }))}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            formData.videoModel === "kling"
                              ? "border-[#8B5CF6] bg-[#8B5CF6]/10"
                              : "border-border/50 bg-secondary/30 hover:border-border"
                          }`}
                          data-testid="simple-model-kling"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Star className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="font-medium text-white text-sm">Kling</span>
                            <span className="text-[10px] px-1 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded">Premium</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Best quality & lip-sync</p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Aspect Ratio Selection */}
                  <div className="space-y-3">
                    <Label className="text-white text-sm">Video Format</Label>
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "9:16" }))}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          formData.aspectRatio === "9:16"
                            ? "border-[#00F0FF] bg-[#00F0FF]/10"
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="aspect-ratio-9-16"
                        title="TikTok, Instagram Reels, YouTube Shorts"
                      >
                        <div className="w-4 h-7 mx-auto mb-1 border-2 border-current rounded-sm" />
                        <span className="text-xs font-medium text-white">9:16</span>
                        <p className="text-[10px] text-muted-foreground">TikTok</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "16:9" }))}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          formData.aspectRatio === "16:9"
                            ? "border-[#00F0FF] bg-[#00F0FF]/10"
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="aspect-ratio-16-9"
                        title="YouTube, Landscape videos"
                      >
                        <div className="w-7 h-4 mx-auto mb-1 border-2 border-current rounded-sm" />
                        <span className="text-xs font-medium text-white">16:9</span>
                        <p className="text-[10px] text-muted-foreground">YouTube</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "1:1" }))}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          formData.aspectRatio === "1:1"
                            ? "border-[#00F0FF] bg-[#00F0FF]/10"
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="aspect-ratio-1-1"
                        title="Instagram Feed, Square format"
                      >
                        <div className="w-5 h-5 mx-auto mb-1 border-2 border-current rounded-sm" />
                        <span className="text-xs font-medium text-white">1:1</span>
                        <p className="text-[10px] text-muted-foreground">Square</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "4:5" }))}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          formData.aspectRatio === "4:5"
                            ? "border-[#00F0FF] bg-[#00F0FF]/10"
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="aspect-ratio-4-5"
                        title="Instagram Feed Portrait"
                      >
                        <div className="w-4 h-5 mx-auto mb-1 border-2 border-current rounded-sm" />
                        <span className="text-xs font-medium text-white">4:5</span>
                        <p className="text-[10px] text-muted-foreground">IG Feed</p>
                      </button>
                    </div>
                  </div>

                  {/* Create Button */}
                  <Button
                    type="button"
                    onClick={handleSimpleModeCreate}
                    disabled={!smartPrompt.trim() || isEnhancing || isProcessingPrompt || loading}
                    className="w-full h-14 text-lg bg-gradient-to-r from-[#00F0FF] to-[#A855F7] hover:opacity-90 text-[#0A0A0B] font-semibold"
                    data-testid="simple-create-btn"
                  >
                    {isEnhancing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing your idea...
                      </>
                    ) : isProcessingPrompt ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing your idea...
                      </>
                    ) : loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Creating video...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        {skipPreview ? "Create Video (Direct)" : "Create Video"}
                      </>
                    )}
                  </Button>

                  {/* Skip Preview Toggle for Power Users */}
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSkipPreview(!skipPreview)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        skipPreview ? 'bg-[#00F0FF]' : 'bg-gray-600'
                      }`}
                      data-testid="skip-preview-toggle"
                      title={skipPreview ? "Preview is disabled - videos will be created directly" : "Enable to skip the preview step"}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                          skipPreview ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className="text-xs text-gray-400">
                      Skip preview {skipPreview && <span className="text-[#00F0FF]">(Power Mode)</span>}
                    </span>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    Want more control?{" "}
                    <button
                      type="button"
                      onClick={() => setIsSimpleMode(false)}
                      className="text-[#A855F7] hover:underline"
                    >
                      Switch to Advanced mode
                    </button>
                  </p>
                </div>
              ) : (
                /* ===== ADVANCED MODE UI (Original) ===== */
                <div className="space-y-6">
              {/* SIMPLIFIED: Single smart input */}
              <div className="space-y-2">
                <Label className="text-white text-lg flex items-center gap-2">
                  What video do you want to create?
                  <span className="text-xs text-cyan-400 font-normal bg-cyan-500/10 px-2 py-0.5 rounded">Smart Input</span>
                </Label>
                <Textarea
                  placeholder="Just describe what you want! Examples:&#10;&#10;• 'Create a video about a man riding a giant bird through magical clouds, epic fantasy style'&#10;&#10;• 'Make a funny TikTok about why cats are better than dogs'&#10;&#10;• Or paste your full script here and we'll detect it automatically!"
                  value={formData.trendDescription}
                  onChange={(e) => {
                    const text = e.target.value;
                    setFormData((prev) => ({ 
                      ...prev, 
                      trendDescription: text,
                    }));
                  }}
                  className="min-h-[140px] bg-secondary/50 border-cyan-500/30 text-base"
                  data-testid="video-description-input"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Paste a full script and we'll auto-detect it, or describe what you want and we'll generate one for you.
                </p>
              </div>

              {/* Editable title field with AI generate button */}
              <div className="space-y-2">
                <Label className="text-white text-sm flex items-center gap-2">
                  Video Title
                  <span className="text-xs text-muted-foreground font-normal">(click generate for AI suggestion)</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter a title for your video..."
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    className="h-10 bg-secondary/50 border-border/50 flex-1"
                    data-testid="video-title-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 px-3 border-cyan-500/50 hover:bg-cyan-500/10 text-cyan-400"
                    onClick={async () => {
                      if (!formData.trendDescription || formData.trendDescription.length < 10) {
                        toast.error("Please describe your video first");
                        return;
                      }
                      try {
                        const response = await generateAPI.generateTitle(formData.trendDescription);
                        if (response.title) {
                          setFormData((prev) => ({ ...prev, title: response.title }));
                          toast.success("Title generated!");
                        }
                      } catch (error) {
                        console.error("Title generation error:", error);
                        toast.error("Failed to generate title");
                      }
                    }}
                    data-testid="generate-title-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Generate
                  </Button>
                </div>
              </div>

              {/* Script Doctor Suggestions - Show hooks as title options */}
              {scriptDoctorSuggestions && scriptDoctorSuggestions.hooks?.length > 0 && (
                <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30" data-testid="script-doctor-suggestions">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <Label className="text-white font-semibold text-base">Script Doctor Suggestions</Label>
                      <p className="text-xs text-muted-foreground">Click to use as title or add to script</p>
                    </div>
                    {scriptDoctorSuggestions.viralScore && (
                      <span className="ml-auto px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                        Viral Score: {scriptDoctorSuggestions.viralScore}/100
                      </span>
                    )}
                  </div>
                  
                  {/* Hooks as title suggestions */}
                  <div className="space-y-2">
                    <Label className="text-xs text-purple-300">Suggested Hooks (click to use as title):</Label>
                    <div className="flex flex-wrap gap-2">
                      {scriptDoctorSuggestions.hooks.slice(0, 5).map((hook, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            const cleanHook = hook.replace(/["""]/g, '').slice(0, 80);
                            setFormData(prev => ({ ...prev, title: cleanHook }));
                            toast.success("Hook applied as title!");
                          }}
                          className="px-3 py-2 text-sm text-left rounded-lg bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 hover:border-purple-400 transition-colors text-white/90"
                          data-testid={`hook-suggestion-${idx}`}
                        >
                          {hook.replace(/["""]/g, '').slice(0, 60)}{hook.length > 60 ? '...' : ''}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* CTAs */}
                  {scriptDoctorSuggestions.ctas?.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-cyan-300">Suggested CTAs (click to append to script):</Label>
                      <div className="flex flex-wrap gap-2">
                        {scriptDoctorSuggestions.ctas.slice(0, 4).map((cta, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              const cleanCta = cta.replace(/["""]/g, '');
                              setFormData(prev => ({ 
                                ...prev, 
                                script: prev.script ? `${prev.script}\n\n${cleanCta}` : cleanCta 
                              }));
                              toast.success("CTA added to script!");
                            }}
                            className="px-3 py-2 text-sm rounded-lg bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:border-cyan-400 transition-colors text-white/90"
                            data-testid={`cta-suggestion-${idx}`}
                          >
                            {cta.replace(/["""]/g, '').slice(0, 50)}{cta.length > 50 ? '...' : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Power Words */}
                  {scriptDoctorSuggestions.powerWords?.length > 0 && (
                    <div className="pt-2 border-t border-purple-500/20">
                      <Label className="text-xs text-muted-foreground">Power words to consider:</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {scriptDoctorSuggestions.powerWords.slice(0, 10).map((word, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs rounded bg-white/5 text-purple-300">
                            {word}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Template Quick Apply - NEW */}
              {brandKit && (
                <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-[#EC4899]/10 to-[#8B5CF6]/10 border border-[#EC4899]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
                        <Layout className="w-4 h-4 text-[#EC4899]" />
                      </div>
                      <div>
                        <Label className="text-white font-semibold text-base">Quick Apply Template</Label>
                        <p className="text-xs text-muted-foreground">Load saved video settings instantly</p>
                      </div>
                    </div>
                    {formData.templateId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, templateId: null }))}
                        className="text-muted-foreground hover:text-white"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  
                  {brandKit.templates?.length > 0 ? (
                    <Select
                      value={formData.templateId || "none"}
                      onValueChange={(templateId) => {
                        if (templateId === "none") {
                          setFormData(prev => ({ ...prev, templateId: null }));
                          return;
                        }
                        
                        const template = brandKit.templates.find(t => t.id === templateId);
                        if (template) {
                          // Apply all template settings
                          setFormData((prev) => ({
                            ...prev,
                            templateId: templateId,
                            videoMode: template.video_mode || prev.videoMode,
                            falVideoStyle: template.video_style || prev.falVideoStyle,
                            voiceOption: template.voice_option || prev.voiceOption,
                            tone: template.tone || prev.tone,
                            brandVoice: template.brand_voice || prev.brandVoice,
                            videoDuration: template.duration ? String(template.duration) : prev.videoDuration,
                            hashtags: template.hashtags?.length > 0 
                              ? [...new Set([...prev.hashtags, ...template.hashtags])]
                              : prev.hashtags,
                            visualDescription: template.visual_description || prev.visualDescription,
                            // Brand kit options from template
                            applyBrandKit: true,
                            useBrandLogo: template.use_logo ?? false,
                            useBrandIntro: template.use_intro ?? false,
                            useBrandOutro: template.use_outro ?? false,
                            useFalAI: template.video_mode === "premium",
                          }));
                          
                          toast.success(`Template "${template.name}" applied!`, {
                            description: "All settings loaded from template.",
                            duration: 3000
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="h-12 bg-secondary/50 border-[#EC4899]/30 hover:border-[#EC4899]/60 transition-colors" data-testid="template-quick-apply">
                        <SelectValue placeholder="Select a template to apply..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Select Template --</SelectItem>
                        {brandKit.templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            <div className="flex items-center gap-2">
                              <span>{template.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({template.video_mode} • {template.video_style})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">No templates saved yet</p>
                      <a href="/brand-kit" className="text-sm text-[#EC4899] hover:underline flex items-center gap-1">
                        <Plus className="w-3 h-3" />
                        Create Template
                      </a>
                    </div>
                  )}
                  
                  {/* Show applied template indicator */}
                  {formData.templateId && brandKit.templates?.find(t => t.id === formData.templateId) && (
                    <div className="flex items-center gap-2 p-2 bg-[#EC4899]/10 rounded-lg">
                      <Check className="w-4 h-4 text-[#EC4899]" />
                      <span className="text-sm text-[#EC4899]">
                        Using: <strong>{brandKit.templates.find(t => t.id === formData.templateId)?.name}</strong>
                      </span>
                    </div>
                  )}
                  
                  {/* Save as Template button */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSaveTemplateModal(true)}
                      className="text-[#EC4899] border-[#EC4899]/50 hover:bg-[#EC4899]/10"
                      data-testid="save-as-template-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Save as Template
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Brand Voice</Label>
                  <Select
                    value={formData.brandVoice}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, brandVoice: value }))}
                  >
                    <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="brand-voice-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="humorous">Humorous</SelectItem>
                      <SelectItem value="inspirational">Inspirational</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Tone</Label>
                  <Select
                    value={formData.tone}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, tone: value }))}
                  >
                    <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="energetic">Energetic</SelectItem>
                      <SelectItem value="calm">Calm</SelectItem>
                      <SelectItem value="authoritative">Authoritative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Target Audience</Label>
                  <Select
                    value={formData.targetAudience}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, targetAudience: value }))}
                  >
                    <SelectTrigger className="h-12 bg-secondary/50 border-border/50" data-testid="target-audience-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="gen-z">Gen Z</SelectItem>
                      <SelectItem value="millennials">Millennials</SelectItem>
                      <SelectItem value="professionals">Professionals</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Video Duration Selector - Made More Visible */}
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-[#00F0FF]/10 to-[#FF0055]/10 border border-[#00F0FF]/30">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#00F0FF]/20 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <Label className="text-white font-semibold text-base">Video Duration</Label>
                </div>
                <Select
                  value={formData.videoDuration}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, videoDuration: value }))}
                >
                  <SelectTrigger className="h-14 bg-secondary/50 border-[#00F0FF]/30 hover:border-[#00F0FF]/60 transition-colors text-base" data-testid="video-duration-select">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (System decides)</SelectItem>
                    <SelectItem value="10">10 Seconds</SelectItem>
                    <SelectItem value="15">15 Seconds</SelectItem>
                    <SelectItem value="20">20 Seconds</SelectItem>
                    <SelectItem value="30">30 Seconds</SelectItem>
                    <SelectItem value="45">45 Seconds</SelectItem>
                    <SelectItem value="60">1 Minute</SelectItem>
                    <SelectItem value="120">2 Minutes</SelectItem>
                    <SelectItem value="180">3 Minutes</SelectItem>
                    <SelectItem value="240">4 Minutes</SelectItem>
                    <SelectItem value="300">5 Minutes</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose how long your video should be. &quot;Auto&quot; will adjust based on your script length.
                </p>
              </div>

              {/* Video Format / Aspect Ratio Selector */}
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-[#FF0055]/10 to-[#A855F7]/10 border border-[#FF0055]/30">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#FF0055]/20 flex items-center justify-center">
                    <Monitor className="w-4 h-4 text-[#FF0055]" />
                  </div>
                  <Label className="text-white font-semibold text-base">Video Format</Label>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "9:16" }))}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      formData.aspectRatio === "9:16"
                        ? "border-[#FF0055] bg-[#FF0055]/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                    data-testid="advanced-aspect-ratio-9-16"
                    title="TikTok, Instagram Reels, YouTube Shorts"
                  >
                    <div className="w-4 h-7 mx-auto mb-1 border-2 border-current rounded-sm" />
                    <span className="text-xs font-medium text-white">9:16</span>
                    <p className="text-[10px] text-muted-foreground">TikTok</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "16:9" }))}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      formData.aspectRatio === "16:9"
                        ? "border-[#FF0055] bg-[#FF0055]/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                    data-testid="advanced-aspect-ratio-16-9"
                    title="YouTube, Landscape videos"
                  >
                    <div className="w-7 h-4 mx-auto mb-1 border-2 border-current rounded-sm" />
                    <span className="text-xs font-medium text-white">16:9</span>
                    <p className="text-[10px] text-muted-foreground">YouTube</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "1:1" }))}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      formData.aspectRatio === "1:1"
                        ? "border-[#FF0055] bg-[#FF0055]/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                    data-testid="advanced-aspect-ratio-1-1"
                    title="Instagram Feed, Square format"
                  >
                    <div className="w-5 h-5 mx-auto mb-1 border-2 border-current rounded-sm" />
                    <span className="text-xs font-medium text-white">1:1</span>
                    <p className="text-[10px] text-muted-foreground">Square</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, aspectRatio: "4:5" }))}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      formData.aspectRatio === "4:5"
                        ? "border-[#FF0055] bg-[#FF0055]/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                    data-testid="advanced-aspect-ratio-4-5"
                    title="Instagram Feed Portrait"
                  >
                    <div className="w-4 h-5 mx-auto mb-1 border-2 border-current rounded-sm" />
                    <span className="text-xs font-medium text-white">4:5</span>
                    <p className="text-[10px] text-muted-foreground">IG Feed</p>
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Select the format that best fits your target platform.
                </p>
              </div>

              {/* Video Generation Mode Selector */}
              <div className="space-y-4 p-3 md:p-4 rounded-xl bg-gradient-to-r from-[#A855F7]/10 via-[#00F0FF]/10 to-[#10B981]/10 border border-[#A855F7]/30">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2 text-sm md:text-base">
                    <Video className="w-4 h-4 text-[#A855F7]" />
                    Video Generation Mode
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Choose how your video will be created. Each mode offers different speed and quality tradeoffs.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Standard Mode */}
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, videoMode: "standard", useFalAI: false }))}
                    disabled={formData.useImageToVideo}
                    className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                      formData.videoMode === "standard" 
                        ? "border-[#10B981] bg-[#10B981]/10" 
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    } ${formData.useImageToVideo ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="mode-standard"
                  >
                    {formData.videoMode === "standard" && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-4 h-4 text-[#10B981]" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <ImageIcon className="w-4 h-4 text-[#10B981]" />
                      <span className="font-medium text-white text-sm">Standard</span>
                    </div>
                    <p className="text-xs text-muted-foreground">AI images with Ken Burns effect</p>
                    <p className="text-xs text-[#10B981] mt-1">~2-3 minutes</p>
                  </button>
                  
                  {/* Fast Mode */}
                  <button
                    type="button"
                    onClick={() => {
                      // Check availability - but default to true if videoModes not loaded
                      const isAvailable = videoModes?.fast?.available ?? true;
                      if (isAvailable) {
                        // Disable lip sync for Fast mode - Kling 2.5 doesn't support it
                        setFormData((prev) => ({ ...prev, videoMode: "fast", useFalAI: false, enableLipSync: false }));
                      } else {
                        toast.error("Fast mode requires API configuration. Please contact support.");
                      }
                    }}
                    disabled={formData.useImageToVideo || (videoModes && !videoModes?.fast?.available)}
                    className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                      formData.videoMode === "fast" 
                        ? "border-[#F59E0B] bg-[#F59E0B]/10" 
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    } ${(formData.useImageToVideo || (videoModes && !videoModes?.fast?.available)) ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="mode-fast"
                  >
                    {formData.videoMode === "fast" && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-4 h-4 text-[#F59E0B]" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-4 h-4 text-[#F59E0B]" />
                      <span className="font-medium text-white text-sm">Fast</span>
                      {videoModes && !videoModes?.fast?.available && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Unavailable</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">HD quality (5-10s clips)</p>
                    <p className="text-xs text-[#F59E0B] mt-1">~1-3 minutes</p>
                  </button>
                  
                  {/* Premium Mode */}
                  <button
                    type="button"
                    onClick={() => {
                      // Check availability - but default to true if videoModes not loaded
                      const isAvailable = videoModes?.premium?.available ?? true;
                      if (isAvailable) {
                        setFormData((prev) => ({ ...prev, videoMode: "premium", useFalAI: true }));
                      } else {
                        toast.error("Premium mode requires API configuration. Please contact support.");
                      }
                    }}
                    disabled={formData.useImageToVideo || (videoModes && !videoModes?.premium?.available)}
                    className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                      formData.videoMode === "premium" 
                        ? "border-[#A855F7] bg-[#A855F7]/10" 
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    } ${(formData.useImageToVideo || (videoModes && !videoModes?.premium?.available)) ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="mode-premium"
                  >
                    {formData.videoMode === "premium" && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-4 h-4 text-[#A855F7]" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-[#A855F7]" />
                      <span className="font-medium text-white text-sm">Premium</span>
                      {videoModes && !videoModes?.premium?.available && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Unavailable</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">High-quality motion video</p>
                    <p className="text-xs text-[#A855F7] mt-1">~3-10 minutes</p>
                  </button>
                </div>
                
                {/* Quality Tier Selector - For Fast Mode */}
                {formData.videoMode === "fast" && (
                  <div className="space-y-3 pt-3 border-t border-border/30">
                    <Label className="text-white text-sm flex items-center gap-2">
                      <Zap className="w-4 h-4 text-[#F59E0B]" />
                      Quality Tier
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">NEW</Badge>
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Pro Quality (Default) */}
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, qualityTier: "pro" }))}
                        className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                          formData.qualityTier === "pro" 
                            ? "border-[#F59E0B] bg-[#F59E0B]/10" 
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="quality-tier-pro"
                      >
                        {formData.qualityTier === "pro" && (
                          <div className="absolute top-2 right-2">
                            <Check className="w-4 h-4 text-[#F59E0B]" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <Star className="w-4 h-4 text-[#F59E0B]" />
                          <span className="font-medium text-white text-sm">Premium Quality</span>
                        </div>
                        <p className="text-xs text-muted-foreground">1080P HD • Kling V2.5 Pro</p>
                        <p className="text-xs text-[#F59E0B] mt-1">Best quality</p>
                      </button>
                      
                      {/* Turbo Standard (Budget-Friendly) */}
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, qualityTier: "turbo" }))}
                        className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                          formData.qualityTier === "turbo" 
                            ? "border-[#10B981] bg-[#10B981]/10" 
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="quality-tier-turbo"
                      >
                        {formData.qualityTier === "turbo" && (
                          <div className="absolute top-2 right-2">
                            <Check className="w-4 h-4 text-[#10B981]" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="w-4 h-4 text-[#10B981]" />
                          <span className="font-medium text-white text-sm">Fast & Affordable</span>
                        </div>
                        <p className="text-xs text-muted-foreground">720P • Kling V3 Turbo</p>
                        <p className="text-xs text-[#10B981] mt-1">~50% cheaper</p>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Premium Mode Sub-Type Selector */}
                {formData.videoMode === "premium" && (
                  <div className="space-y-3 pt-3 border-t border-border/30">
                    <Label className="text-white text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#A855F7]" />
                      Premium Mode Type
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Video + Images (Hybrid) */}
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, premiumType: "hybrid" }))}
                        className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                          formData.premiumType === "hybrid" 
                            ? "border-[#10B981] bg-[#10B981]/10" 
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="premium-type-hybrid"
                      >
                        {formData.premiumType === "hybrid" && (
                          <div className="absolute top-2 right-2">
                            <Check className="w-4 h-4 text-[#10B981]" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-white text-sm">Video + Images</span>
                          <Badge className="bg-[#10B981]/20 text-[#10B981] text-[10px] px-1">Recommended</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">10s premium video + AI images</p>
                        <p className="text-xs text-[#10B981] mt-1 font-medium">1 credit</p>
                      </button>
                      
                      {/* Full Motion Video */}
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, premiumType: "full" }))}
                        className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                          formData.premiumType === "full" 
                            ? "border-[#F59E0B] bg-[#F59E0B]/10" 
                            : "border-border/50 bg-secondary/30 hover:border-border"
                        }`}
                        data-testid="premium-type-full"
                      >
                        {formData.premiumType === "full" && (
                          <div className="absolute top-2 right-2">
                            <Check className="w-4 h-4 text-[#F59E0B]" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-white text-sm">Full Motion Video</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Multiple clips stitched (max 30s)</p>
                        <p className="text-xs text-[#F59E0B] mt-1 font-medium">1 credit per 10s</p>
                      </button>
                    </div>
                    
                    {/* Presentation Style Selector - NEW */}
                    <div className="space-y-2 pt-3 border-t border-border/30">
                      <Label className="text-white text-sm flex items-center gap-2">
                        <span className="text-purple-400">🎬</span>
                        Presentation Style
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Choose how your video is presented
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {/* Auto Detect */}
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, presentationStyle: "auto" }))}
                          className={`p-3 rounded-lg border transition-all text-left ${
                            formData.presentationStyle === "auto" 
                              ? "bg-purple-500/20 border-purple-500/50 ring-1 ring-purple-500/30" 
                              : "bg-secondary/30 border-border/50 hover:bg-secondary/50"
                          }`}
                          data-testid="presentation-style-auto"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">🤖</span>
                            <span className="text-sm font-medium text-white">Auto</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">AI decides based on script</p>
                        </button>
                        
                        {/* Talking Head */}
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, presentationStyle: "talking_head" }))}
                          className={`p-3 rounded-lg border transition-all text-left ${
                            formData.presentationStyle === "talking_head" 
                              ? "bg-purple-500/20 border-purple-500/50 ring-1 ring-purple-500/30" 
                              : "bg-secondary/30 border-border/50 hover:bg-secondary/50"
                          }`}
                          data-testid="presentation-style-talking-head"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">🗣️</span>
                            <span className="text-sm font-medium text-white">Talking Head</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Character speaks on camera</p>
                        </button>
                        
                        {/* Cinematic/Narration */}
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, presentationStyle: "cinematic" }))}
                          className={`p-3 rounded-lg border transition-all text-left ${
                            formData.presentationStyle === "cinematic" 
                              ? "bg-purple-500/20 border-purple-500/50 ring-1 ring-purple-500/30" 
                              : "bg-secondary/30 border-border/50 hover:bg-secondary/50"
                          }`}
                          data-testid="presentation-style-cinematic"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">🎥</span>
                            <span className="text-sm font-medium text-white">Cinematic</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Visuals + voiceover narration</p>
                        </button>
                      </div>
                    </div>
                    
                    {/* Credits Warning */}
                    {formData.premiumType === "full" && creditsNeeded > 1 && (
                      <div className="flex items-center gap-2 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                        <p className="text-xs text-[#F59E0B]">
                          This video will use <span className="font-bold">{creditsNeeded} credits</span>.
                          {creditsRemaining !== null && (
                            <span> You have {creditsRemaining} credit{creditsRemaining !== 1 ? 's' : ''} remaining.</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Visual Description - Show for Fast and Premium modes */}
                {(formData.videoMode === "fast" || formData.videoMode === "premium") && (
                  <div className="space-y-4 pt-3 border-t border-border/30">
                    <div className="space-y-2">
                      <Label className="text-white text-sm flex items-center gap-2">
                        <span className="text-cyan-400">👁️</span>
                        What should the video SHOW?
                        <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px] px-1">Important</Badge>
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Describe the visual scene - what the camera should capture. Be specific about people, actions, settings.
                      </p>
                      <Textarea
                        placeholder="Describe what you want to SEE in the video...

Examples:
• 'A woman in her 30s walking through a sunny park, smiling, wearing casual clothes'
• 'Close-up of hands applying skincare serum, soft bathroom lighting'
• 'A man working at a modern desk with city view in background'

Tip: The more specific you are, the better your video will match your vision!"
                        value={formData.visualDescription}
                        onChange={(e) => setFormData((prev) => ({ ...prev, visualDescription: e.target.value }))}
                        className="min-h-[100px] bg-secondary/50 border-cyan-500/30 focus:border-cyan-500/50 resize-none"
                        data-testid="visual-description-input"
                      />
                      
                      {/* Video Mode Toggle - Simple vs Cinematic */}
                      <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-border/30 mt-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">
                              {formData.useLiteralPrompt ? "Simple Mode" : "Cinematic Mode"}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formData.useLiteralPrompt 
                              ? "Fast & literal - uses your description directly with minimal styling."
                              : "AI-enhanced - adds cinematic flair, mood lighting, and visual polish."
                            }
                          </p>
                        </div>
                        <Switch
                          checked={!formData.useLiteralPrompt}
                          onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, useLiteralPrompt: !checked }))}
                          data-testid="cinematic-mode-toggle"
                        />
                      </div>
                    </div>
                    
                    {/* Video Style - Available for all modes */}
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Video Style</Label>
                      <Select
                        value={formData.falVideoStyle}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, falVideoStyle: value }))}
                      >
                        <SelectTrigger className="h-10 bg-secondary/50 border-border/50" data-testid="video-style-select">
                          <SelectValue placeholder="Select style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cinematic">Cinematic - Professional, dramatic lighting</SelectItem>
                          <SelectItem value="anime">Anime - Animated, vibrant style</SelectItem>
                          <SelectItem value="realistic">Realistic - Photorealistic quality</SelectItem>
                          <SelectItem value="artistic">Artistic - Creative, stylized look</SelectItem>
                          <SelectItem value="cartoon">Cartoon - Fun, playful style</SelectItem>
                          <SelectItem value="documentary">Documentary - Informative, factual style</SelectItem>
                          <SelectItem value="vintage">Vintage - Retro, nostalgic look</SelectItem>
                          <SelectItem value="minimalist">Minimalist - Clean, simple aesthetic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              {/* Brand Kit Section - NEW */}
              <div className="space-y-4 p-3 md:p-4 rounded-xl bg-gradient-to-r from-[#A855F7]/10 to-[#EC4899]/10 border border-[#A855F7]/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <Label className="text-white flex items-center gap-2 text-sm md:text-base">
                      <Palette className="w-4 h-4 text-[#A855F7]" />
                      Apply Brand Kit
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Use your saved brand settings, logo watermark, and intro/outro videos.
                    </p>
                  </div>
                  <Switch
                    checked={formData.applyBrandKit}
                    onCheckedChange={(checked) => {
                      setFormData((prev) => ({ 
                        ...prev, 
                        applyBrandKit: checked,
                        // Auto-enable features if brand kit has them
                        useBrandLogo: checked && brandKit?.logo_url ? true : false,
                        useBrandIntro: checked && brandKit?.intro_video_url ? true : false,
                        useBrandOutro: checked && brandKit?.outro_video_url ? true : false,
                        // Apply brand defaults
                        voiceOption: checked && brandKit?.default_voice ? brandKit.default_voice : prev.voiceOption,
                        falVideoStyle: checked && brandKit?.default_video_style ? brandKit.default_video_style : prev.falVideoStyle,
                        videoMode: checked && brandKit?.default_video_mode ? brandKit.default_video_mode : prev.videoMode,
                      }));
                      
                      // Add default hashtags if brand kit is enabled
                      if (checked && brandKit?.default_hashtags?.length > 0) {
                        setFormData((prev) => {
                          const existingTags = prev.hashtags || [];
                          const newTags = brandKit.default_hashtags.filter(tag => !existingTags.includes(tag));
                          return { ...prev, hashtags: [...existingTags, ...newTags] };
                        });
                      }
                    }}
                    data-testid="apply-brand-kit-toggle"
                    className="self-start sm:self-center"
                  />
                </div>
                
                {/* Brand Kit Options - Show when enabled */}
                {formData.applyBrandKit && brandKit && (
                  <div className="space-y-3 pt-3 border-t border-border/30">
                    {/* Show what will be applied */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Logo Watermark */}
                      <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                        formData.useBrandLogo && brandKit.logo_url
                          ? "border-[#A855F7] bg-[#A855F7]/10"
                          : "border-border/50 bg-secondary/30"
                      } ${!brandKit.logo_url ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <input
                          type="checkbox"
                          checked={formData.useBrandLogo && brandKit.logo_url}
                          onChange={(e) => setFormData((prev) => ({ ...prev, useBrandLogo: e.target.checked }))}
                          disabled={!brandKit.logo_url}
                          className="rounded border-border"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-white">Logo Watermark</span>
                          {brandKit.logo_url ? (
                            <img src={brandKit.logo_url} alt="Logo" className="w-8 h-8 object-contain mt-1 rounded" />
                          ) : (
                            <p className="text-xs text-muted-foreground">No logo uploaded</p>
                          )}
                        </div>
                      </label>
                      
                      {/* Intro Video */}
                      <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                        formData.useBrandIntro && brandKit.intro_video_url
                          ? "border-[#10B981] bg-[#10B981]/10"
                          : "border-border/50 bg-secondary/30"
                      } ${!brandKit.intro_video_url ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <input
                          type="checkbox"
                          checked={formData.useBrandIntro && brandKit.intro_video_url}
                          onChange={(e) => setFormData((prev) => ({ ...prev, useBrandIntro: e.target.checked }))}
                          disabled={!brandKit.intro_video_url}
                          className="rounded border-border"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-white">Intro Video</span>
                          {brandKit.intro_video_url ? (
                            <p className="text-xs text-[#10B981]">{brandKit.intro_duration || 3}s intro</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">No intro uploaded</p>
                          )}
                        </div>
                      </label>
                      
                      {/* Outro Video */}
                      <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                        formData.useBrandOutro && brandKit.outro_video_url
                          ? "border-[#F59E0B] bg-[#F59E0B]/10"
                          : "border-border/50 bg-secondary/30"
                      } ${!brandKit.outro_video_url ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <input
                          type="checkbox"
                          checked={formData.useBrandOutro && brandKit.outro_video_url}
                          onChange={(e) => setFormData((prev) => ({ ...prev, useBrandOutro: e.target.checked }))}
                          disabled={!brandKit.outro_video_url}
                          className="rounded border-border"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-white">Outro Video</span>
                          {brandKit.outro_video_url ? (
                            <p className="text-xs text-[#F59E0B]">{brandKit.outro_duration || 3}s outro</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">No outro uploaded</p>
                          )}
                        </div>
                      </label>
                    </div>
                    
                    {/* Link to Brand Kit page */}
                    <div className="flex justify-end">
                      <a href="/brand-kit" className="text-xs text-[#A855F7] hover:underline flex items-center gap-1">
                        <Palette className="w-3 h-3" />
                        Manage Brand Kit
                      </a>
                    </div>
                  </div>
                )}
                
                {/* No Brand Kit configured message */}
                {formData.applyBrandKit && !brandKit?.brand_name && !brandKit?.logo_url && (
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-muted-foreground text-center">
                      No brand kit configured yet.{" "}
                      <a href="/brand-kit" className="text-[#A855F7] hover:underline">Set up your brand kit</a>
                    </p>
                  </div>
                )}
              </div>

              {/* Image-to-Video Section */}
              <div className="space-y-4 p-3 md:p-4 rounded-xl bg-gradient-to-r from-[#00F0FF]/10 to-[#10B981]/10 border border-[#00F0FF]/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <Label className="text-white flex items-center gap-2 text-sm md:text-base">
                      <ImageIcon className="w-4 h-4 text-[#00F0FF]" />
                      Image to Video
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Upload start/end frames to create smooth transitions and animations.
                    </p>
                  </div>
                  <Switch
                    checked={formData.useImageToVideo}
                    onCheckedChange={(checked) => {
                      if (!checked) {
                        handleRemoveImage();
                        handleRemoveEndImage();
                      }
                      setFormData((prev) => ({ 
                        ...prev, 
                        useImageToVideo: checked,
                        // Disable Premium AI when enabling image-to-video (mutually exclusive)
                        useFalAI: checked ? false : prev.useFalAI
                      }));
                    }}
                    data-testid="image-to-video-toggle"
                    className="self-start sm:self-center"
                    disabled={formData.useFalAI}
                  />
                </div>
                
                {formData.useImageToVideo && (
                  <div className="space-y-4 pt-2 border-t border-border/30">
                    {/* Start Image Upload */}
                    <div className="space-y-2">
                      <Label className="text-white text-sm flex items-center gap-2">
                        <span className="bg-[#00F0FF] text-black text-xs px-2 py-0.5 rounded">START</span>
                        First Frame
                      </Label>
                      {!formData.sourceImagePreview ? (
                        <>
                          <div 
                            className="border-2 border-dashed border-[#00F0FF]/30 rounded-lg p-6 text-center cursor-pointer hover:border-[#00F0FF]/50 transition-colors"
                            onClick={() => imageInputRef.current?.click()}
                          >
                            <input
                              ref={imageInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleImageUpload}
                              className="hidden"
                              data-testid="image-upload-input"
                            />
                            {uploadingImage ? (
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
                                <p className="text-sm text-muted-foreground">Uploading...</p>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-2">
                                <Upload className="w-8 h-8 text-[#00F0FF]" />
                                <p className="text-sm text-white">Click to upload start frame</p>
                                <p className="text-xs text-muted-foreground">JPG, PNG, WebP, GIF (max 10MB)</p>
                              </div>
                            )}
                          </div>
                          <div className="mt-2">
                            <PickFromGalleryButton 
                              onSelect={handleGalleryImageSelect}
                              className="w-full"
                              size="sm"
                            />
                            <MediaPickerButton
                              onSelect={handleGalleryImageSelect}
                              mediaType="image"
                              multiple={false}
                              allowUpload={true}
                              buttonText="Media Library"
                              buttonClassName="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-sm text-purple-300 transition-colors mt-2"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="relative">
                          <img 
                            src={formData.sourceImagePreview} 
                            alt="Start Frame" 
                            className="w-full h-40 object-cover rounded-lg"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 w-8 h-8"
                            onClick={handleRemoveImage}
                            data-testid="remove-image-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* End Image Upload (Optional) - Show only when start image uploaded */}
                    {formData.sourceImagePreview && (
                      <div className="space-y-2">
                        <Label className="text-white text-sm flex items-center gap-2">
                          <span className="bg-[#F59E0B] text-black text-xs px-2 py-0.5 rounded">END</span>
                          End Frame (Interpolation)
                        </Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Upload a second image to create a smooth transition/morphing effect between start and end frames using Kling AI interpolation.
                        </p>
                        {!formData.endImagePreview ? (
                          <div 
                            className="border-2 border-dashed border-[#F59E0B]/30 rounded-lg p-4 text-center cursor-pointer hover:border-[#F59E0B]/50 transition-colors"
                            onClick={() => endImageInputRef.current?.click()}
                          >
                            <input
                              ref={endImageInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleEndImageUpload}
                              className="hidden"
                              data-testid="end-image-upload-input"
                            />
                            {uploadingEndImage ? (
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-6 h-6 text-[#F59E0B] animate-spin" />
                                <p className="text-xs text-muted-foreground">Uploading...</p>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-2">
                                <Upload className="w-6 h-6 text-[#F59E0B]" />
                                <p className="text-xs text-white">+ Add end frame for interpolation</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="relative">
                            <img 
                              src={formData.endImagePreview} 
                              alt="End Frame" 
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2 w-6 h-6"
                              onClick={handleRemoveEndImage}
                              data-testid="remove-end-image-btn"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Motion Prompt */}
                    <div className="space-y-2">
                      <Label className="text-white text-sm flex items-center gap-2">
                        Motion Description
                        <span className="text-xs text-cyan-400 font-normal">(Important!)</span>
                      </Label>
                      <Textarea
                        placeholder="Describe HOW things should MOVE and ANIMATE...&#10;&#10;Examples:&#10;• 'Person slowly turns head and smiles, camera gently zooms in'&#10;• 'Bird takes off and flies across the sky, camera follows'&#10;• 'Smooth transition from day to night, lights turn on gradually'&#10;&#10;Be specific about: movements, camera motion, speed (slow/fast), direction"
                        value={formData.motionPrompt}
                        onChange={(e) => setFormData((prev) => ({ ...prev, motionPrompt: e.target.value }))}
                        className="min-h-[100px] bg-secondary/50 border-border/50 resize-none border-cyan-500/30"
                        data-testid="motion-prompt-input"
                      />
                      <p className="text-xs text-cyan-400/80">
                        This controls how the video animates. Describe movements, camera angles, and transitions.
                      </p>
                    </div>
                    
                    {/* Provider Selection */}
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Generation Mode</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={formData.i2vProvider === "quality" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFormData((prev) => ({ ...prev, i2vProvider: "quality" }))}
                          className={`flex-1 ${formData.i2vProvider === "quality" ? "bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black" : ""}`}
                          data-testid="i2v-provider-quality"
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Premium
                        </Button>
                        <Button
                          type="button"
                          variant={formData.i2vProvider === "speed" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFormData((prev) => ({ ...prev, i2vProvider: "speed" }))}
                          className={`flex-1 ${formData.i2vProvider === "speed" ? "bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black" : ""}`}
                          data-testid="i2v-provider-speed"
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Fast
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formData.i2vProvider === "quality" 
                          ? formData.endImagePreview 
                            ? "Best for smooth motion between your start and end images" 
                            : "High quality animation"
                          : formData.endImagePreview
                            ? "Fast motion between your start and end images"
                            : "Fast animation (~1-2 min)"}
                      </p>
                    </div>
                    
                    <p className="text-xs text-[#00F0FF]">
                      {formData.endImagePreview 
                        ? "Using start + end frames for smooth transition effect"
                        : "Using single image animation mode"}
                    </p>
                  </div>
                )}
              </div>

              {/* Ghost Mode Panel - Viral Ghosting™ */}
              <GhostModePanel
                mode="video"
                isEnabled={ghostModeEnabled}
                onGhostModeToggle={setGhostModeEnabled}
                onGhostProfileSelect={setGhostProfile}
              />

              {/* Stock Footage Toggle (Automatic Mode) */}
              <StockFootageToggle
                isEnabled={stockFootageEnabled}
                onToggle={setStockFootageEnabled}
                onSettingsChange={setStockFootageSettings}
                clonedVoices={clonedVoices}
              />

              {/* Background Music Selector (shown when Stock Footage is enabled) */}
              {stockFootageEnabled && (
                <MusicSelector
                  script={formData.script}
                  selectedMusic={selectedMusic}
                  onMusicSelect={setSelectedMusic}
                  musicMode={musicMode}
                  onMusicModeChange={setMusicMode}
                  musicVolume={musicVolume}
                  onMusicVolumeChange={setMusicVolume}
                />
              )}

              {/* Voiceover Voice Selector with Preview */}
              <div className="space-y-2">
                <Label className="text-white">Voice</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={formData.voiceOption}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, voiceOption: value }))}
                  >
                    <SelectTrigger className="h-12 bg-secondary/50 border-border/50 w-full" data-testid="voice-option-select">
                      <SelectValue placeholder="Select voice" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* AI Native Speech Options */}
                      <div className="px-2 py-1.5 text-xs font-semibold text-blue-400 border-b border-border/30 bg-blue-500/10">
                        No voiceover
                      </div>
                      <SelectItem value="none">Character speaks (its own native voice)</SelectItem>
                      <SelectItem value="visuals-only">No voice (silent video)</SelectItem>
                      
                      {/* User's Cloned Voices */}
                      {clonedVoices.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-primary border-b border-border/30 bg-primary/5 mt-2">
                            🎤 My Voices (Voiceover)
                          </div>
                          {clonedVoices.map((voice) => (
                            <SelectItem key={voice.voice_id} value={`cloned:${voice.voice_id}`}>
                              🎙️ {voice.name} {voice.description ? `- ${voice.description}` : '(My Voice)'}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Preset Voiceover Voices */}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b border-border/30 mt-2">
                        Add a voiceover (lips synced to this voice)
                      </div>
                      <SelectItem value="female-american-rachel">👩 Rachel (American Female) - Calm & Professional</SelectItem>
                      <SelectItem value="female-american-sarah">👩 Sarah (American Female) - Soft & Pleasant</SelectItem>
                      <SelectItem value="female-american-emily">👩 Emily (American Female) - Clear & Calm</SelectItem>
                      <SelectItem value="male-american-josh">👨 Josh (American Male) - Young & Energetic</SelectItem>
                      <SelectItem value="male-american-adam">👨 Adam (American Male) - Deep & Narrative</SelectItem>
                      <SelectItem value="male-american-drew">👨 Drew (American Male) - Versatile</SelectItem>
                      <SelectItem value="male-british-dave">👨 Dave (British Male) - Conversational</SelectItem>
                      <SelectItem value="male-irish-fin">👨 Fin (Irish Male) - Warm & Engaging</SelectItem>
                      <SelectItem value="male-australian-charlie">👨 Charlie (Australian Male) - Casual & Friendly</SelectItem>
                      
                      {/* African Languages (Khaya AI) */}
                      {africanLanguages.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-emerald-400 border-b border-border/30 bg-emerald-500/10 mt-2 flex items-center justify-between">
                            <span>🌍 African Languages (Khaya AI)</span>
                            {favoriteLanguages.length > 0 && (
                              <span className="text-amber-400 flex items-center gap-1">
                                <Star className="w-3 h-3 fill-amber-400" />
                                {favoriteLanguages.length}
                              </span>
                            )}
                          </div>
                          {/* Favorites first */}
                          {sortedAfricanLanguages.map((lang) => {
                            const isFav = favoriteLanguages.includes(lang.id);
                            return (
                              <SelectItem 
                                key={lang.id} 
                                value={lang.id} 
                                data-testid={`voice-${lang.id}`}
                                className="flex items-center"
                              >
                                <span className="flex items-center gap-2">
                                  {isFav && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
                                  {lang.name} - {lang.native}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleVoicePreview}
                    disabled={loadingPreview || formData.voiceOption === "none" || formData.voiceOption === "visuals-only"}
                    className={`h-12 sm:w-12 border-border/50 transition-all flex items-center justify-center gap-2 ${
                      isPlayingPreview 
                        ? 'bg-[#00F0FF]/20 border-[#00F0FF] text-[#00F0FF]' 
                        : 'hover:bg-[#00F0FF]/10 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]'
                    } ${(formData.voiceOption === "none" || formData.voiceOption === "visuals-only") ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="voice-preview-btn"
                    title={(formData.voiceOption === "none" || formData.voiceOption === "visuals-only") ? "No voiceover to preview" : (isPlayingPreview ? "Stop preview" : "Preview voice")}
                  >
                    {loadingPreview ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : isPlayingPreview ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                    <span className="sm:hidden">{isPlayingPreview ? "Stop" : "Preview"}</span>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {formData.voiceOption === "none" 
                    ? "🗣️ AI character will speak naturally - no separate voiceover added."
                    : formData.voiceOption === "visuals-only"
                    ? "🎬 Silent video with beautiful visuals only - add your own audio later."
                    : `🎙️ ${describeVoice(formData.voiceOption, clonedVoices)}. Click the speaker icon to preview.`
                  }
                </p>
                
                {/* Lip-Sync Toggle - Show ONLY for Premium mode (Kling 3.0+). Fast mode (Kling 2.5) doesn't support lip sync */}
                {formData.voiceOption !== "none" && formData.voiceOption !== "visuals-only" && formData.videoMode === "premium" && (
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-border/30 mt-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">Enable Lip-Sync</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#00F0FF]/20 text-[#00F0FF]">AI</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Sync presenter's mouth movements to voiceover. Adds 1-3 min to processing.
                      </p>
                    </div>
                    <Switch
                      checked={formData.enableLipSync}
                      onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, enableLipSync: checked }))}
                      data-testid="enable-lipsync-toggle"
                    />
                  </div>
                )}
              </div>

              {/* Background Music Selection - Using MusicSelector with Jamendo */}
              {!stockFootageEnabled && (
                <MusicSelector
                  script={formData.script}
                  selectedMusic={formData.backgroundMusic}
                  onMusicSelect={(music) => setFormData(prev => ({ 
                    ...prev, 
                    backgroundMusic: music ? { 
                      id: music.id, 
                      name: music.name, 
                      mood: music.mood || "Auto",
                      preview_url: music.preview_url,
                      audiodownload_url: music.audiodownload_url
                    } : null 
                  }))}
                  musicMode={formData.backgroundMusic ? "manual" : "none"}
                  onMusicModeChange={(mode) => {
                    if (mode === "none") {
                      setFormData(prev => ({ ...prev, backgroundMusic: null }));
                    }
                  }}
                  musicVolume={formData.musicVolume}
                  onMusicVolumeChange={(vol) => setFormData(prev => ({ ...prev, musicVolume: vol }))}
                />
              )}

              {/* Custom Music Upload Section */}
              {!stockFootageEnabled && (
                <div className="space-y-4 p-4 bg-secondary/30 rounded-xl border border-border/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Music className="w-5 h-5 text-[#FF0055]" />
                      <h4 className="font-medium text-white">Your Custom Music</h4>
                      <Badge variant="outline" className="text-xs border-[#FF0055]/30 text-[#FF0055]">
                        Premium
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => musicInputRef.current?.click()}
                      disabled={uploadingMusic}
                      className="gap-2 border-[#FF0055]/30 text-[#FF0055] hover:bg-[#FF0055]/10"
                      data-testid="upload-music-btn"
                    >
                      {uploadingMusic ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {uploadingMusic ? "Uploading..." : "Upload Music"}
                    </Button>
                    <input
                      ref={musicInputRef}
                      type="file"
                      accept="audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/x-m4a,.mp3,.wav,.m4a,.aac,.ogg"
                      onChange={handleMusicUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Upload your own music tracks (MP3, WAV, M4A, AAC, OGG). Max 20MB per file.
                  </p>

                  {/* Custom Music List */}
                  {customMusic.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {customMusic.map((music) => (
                        <div
                          key={music.id}
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            backgroundMusic: {
                              id: music.id,
                              name: music.name,
                              mood: "Custom",
                              isCustom: true,
                              file_url: music.file_url
                            }
                          }))}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                            formData.backgroundMusic?.id === music.id
                              ? "bg-[#FF0055]/20 border border-[#FF0055]/50"
                              : "bg-secondary/50 hover:bg-secondary/70 border border-transparent"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              formData.backgroundMusic?.id === music.id
                                ? "bg-[#FF0055]"
                                : "bg-secondary"
                            }`}>
                              <Music className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{music.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {music.duration ? `${Math.floor(music.duration / 60)}:${String(Math.floor(music.duration % 60)).padStart(2, '0')}` : "Custom upload"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {formData.backgroundMusic?.id === music.id && (
                              <Badge className="bg-[#FF0055] text-white text-xs">Selected</Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteCustomMusic(music.id, e)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No custom music uploaded yet</p>
                      <p className="text-xs">Upload your own tracks to use as background music</p>
                    </div>
                  )}
                </div>
              )}
                </div>
              )}
              {/* End of Simple/Advanced mode conditional */}

              {/* Navigation button - only show in Advanced mode */}
              {!isSimpleMode && (
              <div className="flex justify-end">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!formData.title || !formData.trendDescription}
                  className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl"
                  data-testid="next-step-btn"
                >
                  Next: Generate Script
                </Button>
              </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Step 2: Script Generation */}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#00F0FF]" />
                AI Script Generation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl">
                <h4 className="font-medium text-white mb-2">{formData.title}</h4>
                <p className="text-sm text-muted-foreground">{formData.trendDescription}</p>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleGenerateScript}
                  disabled={generatingScript}
                  className="bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90 text-white font-bold rounded-xl gap-2"
                  data-testid="generate-script-btn"
                >
                  {generatingScript ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {generatingScript ? "Generating..." : "Generate Script with AI"}
                </Button>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white flex items-center gap-2">
                    🎤 Voiceover Script
                    <span className="text-xs text-muted-foreground font-normal">(what will be SPOKEN)</span>
                  </Label>
                  {formData.script && formData.script === formData.trendDescription && (
                    <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-400/30">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Auto-detected from description
                    </Badge>
                  )}
                </div>
                <Textarea
                  placeholder="This is what the voiceover will SAY in your video...

Write your narration here, or let AI generate it for you!"
                  value={formData.script}
                  onChange={(e) => setFormData((prev) => ({ ...prev, script: e.target.value }))}
                  className="min-h-[300px] bg-secondary/50 border-border/50 font-mono text-sm"
                  data-testid="script-textarea"
                />
                {formData.script && (
                  <p className="text-xs text-muted-foreground">
                    {formData.script.split(/\s+/).length} words • {formData.script.length} characters
                  </p>
                )}
                
                {/* Script Analysis Hints for Advanced Mode */}
                {creationMode === "advanced" && formData.videoMode === "premium" && scriptAnalysis && (
                  <div className={`mt-2 p-3 rounded-lg border ${
                    scriptAnalysis.is_multi_dialogue ? 'bg-purple-500/10 border-purple-500/30' : 
                    scriptAnalysis.has_scene_description ? 'bg-green-500/10 border-green-500/30' :
                    'bg-blue-500/10 border-blue-500/30'
                  }`}>
                    <div className="flex items-start gap-2">
                      <Sparkles className={`w-4 h-4 mt-0.5 ${
                        scriptAnalysis.is_multi_dialogue ? 'text-purple-400' : 
                        scriptAnalysis.has_scene_description ? 'text-green-400' :
                        'text-blue-400'
                      }`} />
                      <div className="space-y-1">
                        {scriptAnalysis.is_multi_dialogue ? (
                          <>
                            <p className="text-sm text-purple-300 font-medium">
                              Multi-Character Dialogue Detected
                            </p>
                            <p className="text-xs text-purple-300/80">
                              Found {scriptAnalysis.speaker_count} characters: {scriptAnalysis.speakers?.join(", ")}
                            </p>
                          </>
                        ) : scriptAnalysis.has_scene_description ? (
                          <>
                            <p className="text-sm text-green-300 font-medium">
                              Scene with Characters Detected
                            </p>
                            <p className="text-xs text-green-300/80">
                              Will generate cinematic scene (not talking head)
                            </p>
                          </>
                        ) : scriptAnalysis.spoken_word_count > 0 ? (
                          <p className="text-sm text-blue-300 font-medium">
                            Single Speaker Dialogue Detected
                          </p>
                        ) : null}
                        
                        <p className="text-xs text-muted-foreground">
                          Recommended duration: <span className="text-white font-medium">{scriptAnalysis.recommended_duration}s</span>
                          {formData.videoDuration && parseInt(formData.videoDuration) > scriptAnalysis.recommended_duration && (
                            <span className="text-amber-400 ml-2">
                              (current: {formData.videoDuration}s - may cause looping)
                            </span>
                          )}
                        </p>
                        
                        {formData.videoDuration && parseInt(formData.videoDuration) > scriptAnalysis.recommended_duration && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, videoDuration: String(scriptAnalysis.recommended_duration) }))}
                            className="text-xs text-[#00F0FF] hover:underline mt-1"
                          >
                            Apply recommended duration ({scriptAnalysis.recommended_duration}s)
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {creationMode === "advanced" && formData.videoMode === "premium" && isAnalyzingScript && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Analyzing script...
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="border-border/50 rounded-xl"
                >
                  Back
                </Button>
                <Button
                  onClick={handleGoToReview}
                  disabled={generatingHashtags}
                  className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl"
                  data-testid="next-review-btn"
                >
                  {generatingHashtags ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Hashtags...
                    </>
                  ) : (
                    "Next: Review"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Step 3: Review & Create */}
      {step === 3 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Check className="w-5 h-5 text-[#00FF94]" />
                Review & Create
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Title</Label>
                  <p className="text-white font-medium mt-1">{formData.title}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Settings</Label>
                  <p className="text-white mt-1">
                    {formData.brandVoice} / {formData.tone} / {formData.targetAudience}
                  </p>
                </div>
              </div>

              {/* Background Music Summary */}
              {formData.backgroundMusic && (
                <div className="flex items-center gap-3 p-3 bg-[#FF0055]/10 border border-[#FF0055]/30 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-[#FF0055]/20 flex items-center justify-center">
                    <Music className="w-5 h-5 text-[#FF0055]" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-muted-foreground text-xs uppercase tracking-wider">Background Music</Label>
                    <p className="text-white font-medium">{formData.backgroundMusic.name}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{formData.backgroundMusic.mood}</Badge>
                  <span className="text-sm text-muted-foreground">{Math.round(formData.musicVolume * 100)}% vol</span>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wider">Script Preview</Label>
                <div className="mt-2 p-4 bg-secondary/30 rounded-xl max-h-48 overflow-y-auto">
                  <pre className="text-sm text-white/90 whitespace-pre-wrap font-mono">
                    {formData.script}
                  </pre>
                </div>
              </div>

              {/* Hashtags */}
              <div className="space-y-3">
                <Label className="text-white">Hashtags</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add hashtag..."
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddHashtag()}
                    className="h-10 bg-secondary/50 border-border/50"
                    data-testid="hashtag-input"
                  />
                  <Button
                    onClick={handleAddHashtag}
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 border-border/50"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.hashtags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="badge-trending gap-1 cursor-pointer hover:bg-destructive/20"
                      onClick={() => handleRemoveHashtag(tag)}
                    >
                      <Hash className="w-3 h-3" />
                      {tag.replace("#", "")}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="border-border/50 rounded-xl"
                >
                  Back
                </Button>
                <Button
                  onClick={handleCreateVideo}
                  disabled={loading}
                  className="bg-[#00FF94] hover:bg-[#00FF94]/90 text-[#0A0A0B] font-bold rounded-xl gap-2 neon-glow-green"
                  data-testid="create-video-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Create Video
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Upgrade Modal for subscription quota errors */}
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        errorType={upgradeErrorType}
        errorData={upgradeErrorData}
        formData={formData}
        returnPath="/create"
      />
      
      {/* Premium Recommended Modal - shown when Fast mode needs Premium features */}
      <PremiumRecommendedModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        reason={premiumReason}
        onSwitchToPremium={handleSwitchToPremium}
        onContinueWithFast={handleContinueWithFast}
      />
      
      {/* Character Preview Modal - shown before Fast mode video creation */}
      <CharacterPreviewModal
        isOpen={showCharacterPreview}
        onClose={() => {
          setShowCharacterPreview(false);
          setPendingVideoData(null);
          setLoading(false);
        }}
        onApprove={handleCharacterApproved}
        prompt={pendingVideoData?.visual_description || pendingVideoData?.title || ""}
        script={pendingVideoData?.script || ""}
        voiceSummary={pendingVideoData ? describeVoice(pendingVideoData.voice_option, clonedVoices) : ""}
        style="photorealistic"
      />
      
      {/* Save as Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1A2E] rounded-xl p-6 max-w-md w-full border border-[#EC4899]/30">
            <h3 className="text-xl font-bold text-white mb-4">Save as Template</h3>
            <p className="text-gray-400 text-sm mb-4">
              Save your current settings as a reusable template for quick video creation.
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2 block">Template Name</Label>
                <Input
                  value={saveTemplateName}
                  onChange={(e) => setSaveTemplateName(e.target.value)}
                  placeholder="e.g., Product Demo Style"
                  className="bg-secondary/50 border-border/50"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Description (Optional)</Label>
                <Textarea
                  value={saveTemplateDescription}
                  onChange={(e) => setSaveTemplateDescription(e.target.value)}
                  placeholder="Describe when to use this template..."
                  className="bg-secondary/50 border-border/50 min-h-[80px]"
                  data-testid="template-description-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveTemplateModal(false);
                  setSaveTemplateName("");
                  setSaveTemplateDescription("");
                }}
                data-testid="cancel-template-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate || !saveTemplateName.trim()}
                className="bg-[#EC4899] hover:bg-[#EC4899]/80"
                data-testid="confirm-save-template-btn"
              >
                {savingTemplate ? "Saving..." : "Save Template"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}